import Phaser from 'phaser';
import { getShieldForPlayer } from './Utils';
import { CommandDurations } from './chat/CommandConfig';
import { socketService } from './services/SocketService';

const DEFAULT_CONTAINER_CAPACITY = 9;
const CONSUMABLE_CONTAINERS = [
  {
    key: 'healing_container',
    matches: (itemDef) =>
      !!itemDef &&
      ((itemDef.effect && typeof itemDef.effect === 'object' && itemDef.effect.heal != null) ||
        itemDef.type === 'healing'),
  },
  {
    key: 'magic_container',
    matches: (itemDef) =>
      !!itemDef &&
      ((itemDef.effect && typeof itemDef.effect === 'object' && itemDef.effect.mana != null) ||
        itemDef.type === 'mana'),
  },
  {
    key: 'battle_container',
    matches: (itemDef) =>
      !!itemDef &&
      ((itemDef.effect &&
        typeof itemDef.effect === 'object' &&
        (itemDef.effect.attackBoost != null ||
          itemDef.effect.rage != null)) ||
        itemDef.type === 'strength' ||
        itemDef.type === 'battle'),
  },
  {
    key: 'arrow_container',
    matches: (itemDef) =>
      !!itemDef &&
      ((itemDef.effect &&
        typeof itemDef.effect === 'object' &&
        itemDef.effect.arrows != null) ||
        itemDef.type === 'ammo' ||
        itemDef.type === 'arrow'),
  },
  {
    key: 'poison_container',
    matches: (itemDef) =>
      !!itemDef &&
      ((itemDef.effect &&
        typeof itemDef.effect === 'object' &&
        (itemDef.effect.dot != null || itemDef.effect.poison != null)) ||
        itemDef.type === 'poison'),
  },
];

function determineConsumableContainer(itemDef) {
  if (!itemDef) return null;
  for (const container of CONSUMABLE_CONTAINERS) {
    if (container.matches(itemDef)) {
      return container.key;
    }
  }
  return null;
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

const clamp01 = (value) => Math.max(0, Math.min(1, value));

const lerp = (min, max, t) => min + (max - min) * t;

function ensureInventoryEntryMetadata(entry, overrides = {}) {
  if (!entry) return entry;
  const quantity = Math.max(
    0,
    toNumber(
      overrides.quantity ??
        entry.quantity ??
        entry.qty ??
        entry.amount ??
        overrides.qty,
      0
    )
  );

  entry.itemId = entry.itemId ?? entry.item_id ?? null;
  entry.item_id = entry.itemId;
  entry.itemType = entry.itemType ?? entry.item_type ?? null;
  entry.item_type = entry.itemType;

  entry.equipmentSlot = entry.equipmentSlot ?? entry.equipment_slot ?? null;
  entry.equipment_slot = entry.equipmentSlot;

  entry.slotNumber = entry.slotNumber ?? entry.slot_number ?? null;
  entry.slot_number = entry.slotNumber;

  entry.qty = quantity;
  entry.quantity = quantity;

  entry.initialQty = Math.max(
    0,
    toNumber(overrides.initialQty ?? entry.initialQty, quantity)
  );
  entry.originalQty = Math.max(
    0,
    toNumber(overrides.originalQty ?? entry.originalQty, entry.initialQty)
  );
  entry.lootQty = Math.max(
    0,
    toNumber(overrides.lootQty ?? entry.lootQty, 0)
  );
  entry.consumedOriginal = Math.max(
    0,
    toNumber(overrides.consumedOriginal ?? entry.consumedOriginal, 0)
  );
  entry.consumedLoot = Math.max(
    0,
    toNumber(overrides.consumedLoot ?? entry.consumedLoot, 0)
  );
  return entry;
}

function incrementConsumptionRecord(map, itemId, kind, amount = 1) {
  if (!itemId || !map) return;
  const key = String(itemId);
  const record = map.get(key) || { consumedOriginal: 0, consumedLoot: 0 };
  if (kind === 'original') {
    record.consumedOriginal += amount;
  } else if (kind === 'loot') {
    record.consumedLoot += amount;
  }
  map.set(key, record);
}

export class Player {
  constructor(scene, x, y, textureKey, name, walletAddress = null) {
    this.scene = scene;
    const objectScale = (typeof scene.objectScale === 'number') ? scene.objectScale : 1.0;
    this.sprite = scene.add.image(x, y, textureKey);
    this.sprite.setScale(0.12 * objectScale); // scale relative to map size
    this.sprite.setDepth(5);
    // Ensure UI camera does not render world player sprite
    if (scene.uiCamera && scene.uiCamera.ignore) {
      scene.uiCamera.ignore(this.sprite);
    }

    this.name = name;
    this.walletAddress = walletAddress;
    this.health = 200;
    this.maxHealth = 200;
    this.isAlive = true;
    this.lastAttackWasCritical = false;

    // Hero integration
    this.heroId = null;
    this.userId = null;
    this.strategyAggression = 50; // 0-100, affects attack vs defense behavior
    this.strategyLooting = 50;    // 0-100, affects food seeking vs combat priority
    this.strategyPositioning = 50; // 0-100, affects edge vs center target selection

    // Team system
    this.team = null; // null, 'red', 'blue', 'green', 'yellow', etc.
    this.teamColor = null; // hex color for team identification

    // Hero primary attributes (default 10 each for non-hero players)
    this.strength = 10;
    this.endurance = 10;
    this.coordination = 10;
    this.quickness = 10;
    this.focus = 10;
    this.self = 10;

    // Equipped weapon and armor data
    this.equippedWeapon = null; // Will store weapon definition object
    this.equippedArmor = {
      head: null,
      chest: null,
      legs: null
    };

    // Stat modifiers from items/potions/effects
    this.statModifiers = {
      strength: 0, endurance: 0, coordination: 0,
      quickness: 0, focus: 0, self: 0,
      damage: 0, defense: 0, attackSpeed: 0
    };

    // Battle statistics
    this.eliminations = 0;
    this.damageDealt = 0;
    this.damageTaken = 0;
    this.deathOrder = null;
    this.deathTimestamp = null;
    this.timeAliveSeconds = null;

    // Resource pools
    this.class = 'fighter';
    this.maxStamina = 100;
    this.stamina = this.maxStamina;
    this.maxMana = 100;
    this.mana = this.maxMana;
    this.healthRegenRate = 0.005; // 0.5% per second
    this.manaRegenRate = 0.05;   // 5% per second
    this.staminaRegenRate = 0.05; // 5% per second
    this.lastResourceUpdateAt = this.getNow();
    this.combatStateGraceMs = 2500;
    this.inCombatUntil = 0;
    this.ultHealLockoutUntil = 0;
    this.lastItemHealAmount = 0;
    this.lastItemManaAmount = 0;
    this._sidebarResourceDirty = false;
    this._lastSidebarSyncAt = 0;

    // Create name text
    this.nameText = scene.add.text(x, y - 43 * objectScale, name, {
      fontSize: '24px',
      fontFamily: 'Arial Black, Arial, sans-serif',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 4,
      shadow: {
        offsetX: 1,
        offsetY: 1,
        color: '#000000',
        blur: 2,
        fill: true
      }
    }).setOrigin(0.5).setDepth(12);
    if (scene.uiCamera && scene.uiCamera.ignore) {
      scene.uiCamera.ignore(this.nameText);
    }

    // Create shield sprite (initially hidden, shown for top holders)
    this.shieldSprite = scene.add.image(x, y - 55 * objectScale, 'shield-grey');
    this.shieldSprite.setScale(0.156 * objectScale);
    this.shieldSprite.setDepth(11);
    this.shieldSprite.setVisible(false);
    if (scene.uiCamera && scene.uiCamera.ignore) {
      scene.uiCamera.ignore(this.shieldSprite);
    }

    // Create health bar
    this.healthBarBg = scene.add.graphics();
    this.healthBar = scene.add.graphics();
    this.healthBarBg.setDepth(8);
    this.healthBar.setDepth(9);
    if (scene.uiCamera && scene.uiCamera.ignore) {
      scene.uiCamera.ignore([this.healthBarBg, this.healthBar]);
    }
    this.updateHealthBar();

    this.targetX = x;
    this.targetY = y;
    this.moveSpeed = 20 * objectScale; // scale movement speed with map scale
    this.baseMoveSpeed = this.moveSpeed;
    this.lastMoveTime = 0;
    this.lastAttackTime = 0;
    this.lastRangedAttackTime = 0;
    this.arrowCount = 0;
    this.maxArrows = 120;
    // Cache combat reach scaled to objectScale so per-player logic can use it if needed
    this.meleeRange = 52 * objectScale;
    this.isBattling = false;
    this.isCharging = false; // Track if player is charging (for charging bonus on first attack)
    this.inventory = []; // { itemId, qty }
    this.strategyItemUse = 50; // 0 conservative, 100 liberal
    this.lastAutoUseAt = 0;
    this.commandState = {};
    this.commandCooldowns = {};
    this.invulnerableUntil = 0; // Track invulnerability expiration time
    this.speedModifiers = new Map();
    this.pendingCommandTimeouts = new Set();
    this.refreshMoveSpeed();
    this.nextItemCheckAt = Date.now() + 1000 + Math.floor(Math.random() * 1000);
    this.consumptionTotals = new Map();
    
    // Chat bubble components (initially hidden)
    this.tauntBubble = null;
    this.tauntText = null;
    this.tauntTimer = 0;
    this.tauntOnComplete = null;
    this.taunts = {}; // Event-grouped taunts, populated by TauntManager

    // Ultimate system
    this.ultimateType = null; // 'damage_burst', 'area_burst'
    this.ultimateCharge = 0; // 0-100
    this.lastUltimateUpdateAt = this.getNow();
    this.lastDamageEventAt = this.getNow();
    this.initializeUltimate();
    
    // Ultimate icon (shown when ready, positioned to left of health bar)
    const iconMap = { damage_burst: '⚔️', area_burst: '💥' };
    this.ultimateIcon = scene.add.text(x, y, iconMap[this.ultimateType] || '⭐', {
      fontSize: '24px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(1, 0.5).setDepth(10).setVisible(false);
    if (scene.uiCamera && scene.uiCamera.ignore) {
      scene.uiCamera.ignore(this.ultimateIcon);
    }
  }

  getNow() {
    return this.scene?.time?.now || Date.now();
  }

  getItemUsageFactor() {
    const raw = Number(this.strategyItemUse ?? 0);
    return clamp01(raw / 100);
  }

  isCommandOnCooldown(name) {
    if (!name) return false;
    const expiresAt = this.commandCooldowns?.[name] || 0;
    return expiresAt > this.getNow();
  }

  getCommandCooldownRemaining(name) {
    if (!name) return 0;
    const expiresAt = this.commandCooldowns?.[name] || 0;
    return Math.max(0, expiresAt - this.getNow());
  }

  startCommandCooldown(name, durationMs = 0) {
    if (!name) return;
    const now = this.getNow();
    const expiresAt = now + Math.max(0, durationMs || 0);
    this.commandCooldowns[name] = expiresAt;
  }

  refreshMoveSpeed() {
    let multiplier = 1;
    if (this.speedModifiers && this.speedModifiers.size) {
      this.speedModifiers.forEach(entry => {
        multiplier *= entry.factor;
      });
    }
    // Apply global 20% speed increase
    multiplier *= 1.2;
    this.moveSpeed = this.baseMoveSpeed * multiplier;
  }

  applySpeedModifier(id, factor = 1, durationMs = 0) {
    if (!id) return;
    if (!this.speedModifiers) this.speedModifiers = new Map();
    const existing = this.speedModifiers.get(id);
    if (existing) {
      if (existing.timer?.remove) {
        existing.timer.remove(false);
      }
      if (existing.timeoutId) {
        clearTimeout(existing.timeoutId);
      }
    }

    const entry = { factor: factor || 1, timer: null, timeoutId: null };
    this.speedModifiers.set(id, entry);
    this.refreshMoveSpeed();

    const duration = Math.max(0, durationMs || 0);
    if (duration > 0) {
      if (this.scene?.time?.delayedCall) {
        entry.timer = this.scene.time.delayedCall(duration, () => {
          this.speedModifiers.delete(id);
          this.refreshMoveSpeed();
        });
      } else {
        const timeoutId = setTimeout(() => {
          this.pendingCommandTimeouts?.delete(timeoutId);
          this.speedModifiers.delete(id);
          this.refreshMoveSpeed();
        }, duration);
        entry.timeoutId = timeoutId;
        this.pendingCommandTimeouts?.add(timeoutId);
      }
    }
  }

  removeSpeedModifier(id) {
    if (!id || !this.speedModifiers) return;
    const entry = this.speedModifiers.get(id);
    if (entry) {
      if (entry.timer?.remove) {
        entry.timer.remove(false);
      }
      if (entry.timeoutId) {
        clearTimeout(entry.timeoutId);
        this.pendingCommandTimeouts?.delete(entry.timeoutId);
      }
      this.speedModifiers.delete(id);
      this.refreshMoveSpeed();
    }
  }

  clearPendingCommandTimeouts() {
    if (!this.pendingCommandTimeouts || !this.pendingCommandTimeouts.size) return;
    this.pendingCommandTimeouts.forEach(id => {
      try { clearTimeout(id); } catch (_) {}
    });
    this.pendingCommandTimeouts.clear();
  }

  initializeInventory(entries) {
    this.consumptionTotals = new Map();
    const source = Array.isArray(entries) ? entries : this.inventory;
    if (!Array.isArray(source)) {
      return;
    }
    source.forEach((entry) => ensureInventoryEntryMetadata(entry));
    this.inventory = source;
  }

  getConsumptionSummary() {
    const summary = [];
    if (!this.consumptionTotals) return summary;
    this.consumptionTotals.forEach((value, key) => {
      summary.push({
        itemId: key,
        consumedOriginal: value.consumedOriginal || 0,
        consumedLoot: value.consumedLoot || 0,
      });
    });
    if (summary.length > 0) {
      console.log(`[Player.getConsumptionSummary] 📊 Consumption summary for ${this.name} (${this.heroId}):`, {
        playerName: this.name,
        heroId: this.heroId,
        totalItemsConsumed: summary.length,
        consumption: summary,
        totalOriginalConsumed: summary.reduce((sum, s) => sum + (s.consumedOriginal || 0), 0),
        totalLootConsumed: summary.reduce((sum, s) => sum + (s.consumedLoot || 0), 0)
      });
    }
    return summary;
  }

  triggerEmoteBurst(token, count = CommandDurations.emoteBurstCount, interval = CommandDurations.emoteBurstIntervalMs) {
    if (!this.scene || typeof this.scene.showEmote !== 'function') return;
    const total = Math.max(1, count || 1);
    const spacing = Math.max(80, interval || 80);
    for (let i = 0; i < total; i++) {
      const call = () => {
        try { this.scene.showEmote(this, token); } catch (_) {}
      };
      if (this.scene.time?.delayedCall) {
        this.scene.time.delayedCall(i * spacing, call);
      } else {
        const timeoutId = setTimeout(() => {
          this.pendingCommandTimeouts?.delete(timeoutId);
          call();
        }, i * spacing);
        this.pendingCommandTimeouts?.add(timeoutId);
      }
    }
  }

  findNearestEnemy(players = this.scene?.players || []) {
    const list = Array.isArray(players) ? players : [];
    const px = this.sprite?.x ?? this.targetX ?? 0;
    const py = this.sprite?.y ?? this.targetY ?? 0;
    let best = null;
    let bestDist = Infinity;
    list.forEach(p => {
      if (!p || p === this || !p.isAlive || !p.sprite) return;
      if (this.isTeammate && this.isTeammate(p)) return;
      const dist = Phaser.Math.Distance.Between(px, py, p.sprite.x, p.sprite.y);
      if (dist < bestDist) {
        bestDist = dist;
        best = p;
      }
    });
    return best;
  }

  seekNearestFood(foodList = this.scene?.food || [], bounds = this.scene?.gameManager?.worldBounds) {
    const list = Array.isArray(foodList) ? foodList : [];
    if (!list.length) return false;
    const px = this.sprite?.x ?? this.targetX ?? 0;
    const py = this.sprite?.y ?? this.targetY ?? 0;
    let best = null;
    let bestDist = Infinity;
    list.forEach(item => {
      if (!item || !item.active) return;
      if (bounds && typeof bounds.contains === 'function' && !bounds.contains(item.x, item.y)) return;
      const dist = Phaser.Math.Distance.Between(px, py, item.x, item.y);
      if (dist < bestDist) {
        bestDist = dist;
        best = item;
      }
    });
    if (best) {
      this.targetX = best.x;
      this.targetY = best.y;
      return true;
    }
    return false;
  }

  consumeFirstItemOfType(type) {
    if (!type) return false;
    const inv = Array.isArray(this.inventory) ? this.inventory : [];
    if (!inv.length) return false;
    const itemsById = this.scene?.itemsById;
    for (let i = 0; i < inv.length; i++) {
      const entry = inv[i];
      let def = null;
      if (itemsById && typeof itemsById.get === 'function') {
        def = itemsById.get(entry.itemId);
      }
      if (!def && entry?.itemDef) def = entry.itemDef;
      if (!def || def.type !== type || (entry.qty || 0) <= 0) continue;
      if (this.consumeItem(def, i)) {
        return true;
      }
    }
    return false;
  }

  getNaturalHealAmount() {
    const focus = typeof this.getEffectiveFocus === 'function' ? this.getEffectiveFocus() : (this.focus || 0);
    const endurance = typeof this.getEffectiveEndurance === 'function' ? this.getEffectiveEndurance() : (this.endurance || 0);
    const base = 20 + focus * 1.2 + endurance * 0.8;
    return Math.round(base);
  }

  attackCommand(options = {}) {
    if (!this.isAlive) return false;
    if (this.isStunned()) return false; // Cannot attack while stunned
    this.stopShooting(); // Cancel shooting when attacking
    const duration = Math.max(1000, options.durationMs || CommandDurations.attackBiasMs);
    this.setPrioritizeAttack(duration);
    this.commandState.attackTarget = this.findNearestEnemy();
    if (this.scene?.sfxManager?.playCategory) {
      const rate = Phaser.Math.FloatBetween(0.95, 1.05);
      this.scene.sfxManager.playCategory('scream_mad', this, { rate });
    }
    this.triggerEmoteBurst('⚔️');
    if (typeof this.scene?.requestActionFocus === 'function') {
      this.scene.requestActionFocus(this, { durationMs: CommandDurations.attackFocusMs, zoom: 0.7 });
    }
    return true;
  }

  lootCommand(options = {}) {
    if (!this.isAlive) return false;
    this.stopShooting(); // Cancel shooting when looting
    const duration = Math.max(1000, options.durationMs || CommandDurations.lootBiasMs);
    this.setPrioritizeLoot(duration);
    if (this.scene?.sfxManager?.playCategory) {
      const rate = Phaser.Math.FloatBetween(0.95, 1.05);
      this.scene.sfxManager.playCategory('scream_mad', this, { rate });
    }
    this.triggerEmoteBurst('💰');
    return true;
  }

  setPrioritizeLoot(durationMs) {
    const now = this.getNow();
    const duration = Math.max(1000, durationMs || CommandDurations.lootBiasMs);
    this.commandState = this.commandState || {};
    this.commandState.prioritizeLootUntil = Math.max(this.commandState.prioritizeLootUntil || 0, now + duration);
    this.applySpeedModifier('lootBoost', 1.5, duration);
    try { console.log('[ChatCmd] prioritizeLootUntil', { player: this.name, until: this.commandState.prioritizeLootUntil }); } catch (_) {}
  }

  defendCommand(durationMs = CommandDurations.defendDurationMs) {
    if (!this.isAlive) return false;
    const duration = Math.max(1000, durationMs || CommandDurations.defendDurationMs);
    const now = this.getNow();
    this.commandState.defendUntil = Math.max(this.commandState.defendUntil || 0, now + duration);
    this.applyStatModifier('defense', 0.35, duration);
    this.applyStatModifier('damage', -3, duration);
    this.applySpeedModifier('defend', 0.7, duration);
    this.triggerEmoteBurst('🛡️');
    return true;
  }

  activateRage(durationMs) {
    const now = this.getNow();
    const duration = Math.max(1000, durationMs || CommandDurations.rageDurationMs);
    this.commandState.rageUntil = Math.max(this.commandState.rageUntil || 0, now + duration);
    const boostTarget = now + duration;
    if (this.commandState.strengthBuff) {
      this.commandState.strengthBuff.attackBoost = Math.max(this.commandState.strengthBuff.attackBoost || 0, 0.5);
      this.commandState.strengthBuff.until = Math.max(this.commandState.strengthBuff.until || 0, boostTarget);
    } else {
      this.commandState.strengthBuff = {
        attackBoost: 0.5,
        until: boostTarget
      };
    }
    this.applySpeedModifier('rage', 1.25, duration);
    this.triggerEmoteBurst('🔥');
    if (this.scene?.sfxManager?.playCategory) {
      const rate = Phaser.Math.FloatBetween(0.9, 1.08);
      this.scene.sfxManager.playCategory('scream_big', this, { rate });
    }
  }

  rageCommand() {
    if (!this.isAlive) return false;
    this.stopShooting(); // Cancel shooting when raging
    let enraged = false;

    if (!this.isCommandOnCooldown('rage')) {
      this.activateRage(CommandDurations.rageDurationMs);
      this.startCommandCooldown('rage', CommandDurations.rageCooldownMs);
      enraged = true;
    }

    if (!enraged && this.inventory && this.inventory.length) {
      const potionUsed = this.consumeFirstItemOfType('strength');
      if (potionUsed) {
        this.activateRage(CommandDurations.rageDurationMs);
        enraged = true;
      }
    }

    return enraged;
  }

  centerCommand() {
    if (!this.isAlive) return false;
    const now = this.getNow();
    this.commandState = this.commandState || {};
    
    // Get camera focus position from camera controller
    const cameraController = this.scene?.cameraController;
    if (cameraController && typeof cameraController.focusX === 'number' && typeof cameraController.focusY === 'number') {
      this.commandState.centerTargetX = cameraController.focusX;
      this.commandState.centerTargetY = cameraController.focusY;
      this.commandState.centerUntil = Math.max(this.commandState.centerUntil || 0, now + 10000); // 10 seconds
      if (this.scene?.showEmote) this.scene.showEmote(this, '🎯');
      return true;
    } else {
      // Fallback to world bounds center if camera controller not available
      const bounds = this.scene?.gameManager?.worldBounds;
      if (bounds) {
        this.commandState.centerTargetX = bounds.centerX;
        this.commandState.centerTargetY = bounds.centerY;
        this.commandState.centerUntil = Math.max(this.commandState.centerUntil || 0, now + 10000);
        if (this.scene?.showEmote) this.scene.showEmote(this, '🎯');
        return true;
      }
    }
    return false;
  }

  applyStun(baseDurationMs = 3000) {
    if (!this.isAlive) return;
    const now = this.getNow();
    this.commandState = this.commandState || {};
    
    // Calculate stun duration: base 3 seconds, reduced by Self attribute
    // Higher Self = shorter stun, but minimum 1 second
    const effectiveSelf = this.getEffectiveSelf ? this.getEffectiveSelf() : (this.self || 10);
    // Self reduces stun: each point above 10 reduces stun by 1% (max 50% reduction at 60 Self)
    // Formula: stunDuration = baseDuration * (1 - min(0.5, (self - 10) * 0.01))
    const selfReduction = Math.min(0.5, Math.max(0, (effectiveSelf - 10) * 0.01));
    let stunDuration = baseDurationMs * (1 - selfReduction);
    // Ensure minimum 1 second stun
    stunDuration = Math.max(1000, stunDuration);
    
    this.commandState.stunnedUntil = Math.max(this.commandState.stunnedUntil || 0, now + stunDuration);
  }

  isStunned() {
    if (!this.isAlive) return false;
    const now = this.getNow();
    const cs = this.commandState || {};
    return cs.stunnedUntil && now < cs.stunnedUntil;
  }

  healCommand() {
    if (!this.isAlive) return false;
    const now = this.getNow();
    if (this.ultHealLockoutUntil && now < this.ultHealLockoutUntil) {
      this.triggerEmoteBurst('⏳');
      return false;
    }

    let healed = false;
    let totalHealed = 0;

    if (!this.isCommandOnCooldown('healNatural')) {
      const amount = this.getNaturalHealAmount();
      if (amount > 0) {
        const actual = this.heal(amount);
        if (actual > 0) {
          this.startCommandCooldown('healNatural', CommandDurations.healNaturalCooldownMs);
          healed = true;
          totalHealed += actual;
        }
      }
    }

    if (!healed) {
      this.lastItemHealAmount = 0;
      healed = this.consumeFirstItemOfType('healing');
      if (healed) {
        totalHealed += this.lastItemHealAmount || 0;
      }
      this.lastItemHealAmount = 0;
    }

    if (this.scene?.sfxManager?.onBreath) {
      this.scene.sfxManager.onBreath(this);
    }

    if (healed) {
      if (totalHealed > 0) {
        this.restoreStamina(totalHealed);
      }
      this.triggerEmoteBurst('❤️');
      if (typeof this.scene?.requestActionFocus === 'function') {
        this.scene.requestActionFocus(this, { durationMs: CommandDurations.attackFocusMs, zoom: 0.7 });
      }
      return true;
    }

    this.setPreferHeal(CommandDurations.healBiasMs, { force: true });
    this.seekNearestFood(this.scene?.food || [], this.scene?.gameManager?.worldBounds);
    
    if (typeof this.scene?.requestActionFocus === 'function') {
      this.scene.requestActionFocus(this, { durationMs: CommandDurations.attackFocusMs, zoom: 0.7 });
    }
    return false;
  }

  manaCommand() {
    if (!this.isAlive) return false;
    const used = this.consumeFirstItemOfType('mana');
    if (!used) return false;
    this.triggerEmoteBurst('🔷');
    if (typeof this.scene?.requestActionFocus === 'function') {
      this.scene.requestActionFocus(this, { durationMs: 1500, zoom: 0.8 });
    }
    return true;
  }

  runCommand() {
    if (!this.isAlive) return false;
    if (this.isCommandOnCooldown('run')) {
      return false;
    }
    // Check if player has enough stamina to run (requires 10 stamina)
    const runStaminaCost = 10;
    if ((this.stamina || 0) < runStaminaCost) {
      return false;
    }
    this.startCommandCooldown('run', CommandDurations.runCooldownMs);
    const success = this.setRunAway(CommandDurations.runDurationMs, CommandDurations.runCooldownMs);
    if (success) {
      // Consume stamina for running
      this.spendStamina(runStaminaCost);
      this.triggerEmoteBurst('💨');
    }
    return success;
  }

  shootCommand() {
    if (!this.isAlive) return false;
    if (this.isStunned()) return false; // Cannot shoot while stunned
    this.commandState = this.commandState || {};
    const now = this.getNow();
    const active = !!this.commandState.shootModeActive;
    if (active) {
      this.commandState.shootModeActive = false;
      this.commandState.shootModeToggledAt = now;
      if (this.scene?.showEmote) this.scene.showEmote(this, '🛑');
      return false;
    }
    if (!this.hasArrows()) {
      if (this.scene?.showEmote) this.scene.showEmote(this, '❌');
      return false;
    }
    if (!this.getArrowStats() && (this.arrowCount || 0) <= 0) {
      if (this.scene?.showEmote) this.scene.showEmote(this, '⚠️');
      return false;
    }
    this.commandState.shootModeActive = true;
    this.commandState.shootModeToggledAt = now;
    if (this.scene?.showEmote) this.scene.showEmote(this, '🏹');
    return true;
  }

  stopShooting(reason = '') {
    if (!this.commandState?.shootModeActive) return;
    this.commandState.shootModeActive = false;
    if (reason === 'no_arrows' && this.scene?.showEmote) {
      this.scene.showEmote(this, '⚠️');
    }
    // Clear target to trigger new wander destination on next update
    // This ensures movement resumes when shooting stops
    this.targetX = null;
    this.targetY = null;
  }

  addArrows(amount = 0) {
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    const before = this.arrowCount;
    this.arrowCount = Math.min(this.maxArrows, this.arrowCount + amount);
    if (this.scene?.showEmote) this.scene.showEmote(this, '🏹');
    this.markSidebarResourcesDirty();
    return this.arrowCount - before;
  }

  consumeArrow() {
    // First, try to consume from arrowCount (runtime ammo counter)
    if ((this.arrowCount || 0) > 0) {
      this.arrowCount = Math.max(0, this.arrowCount - 1);
      if (this.arrowCount === 0 && !this.hasArrowItem()) {
        this.stopShooting('no_arrows');
      }
      this.markSidebarResourcesDirty();
      return true;
    }
    
    // If arrowCount is 0, try to consume from inventory items (like potions)
    if (this.hasArrowItem()) {
      const arrowInfo = this.getBestArrowInventoryEntry();
      if (arrowInfo && arrowInfo.entry && arrowInfo.definition) {
        // Find the inventory index
        const invIndex = this.inventory.findIndex(e => e.itemId === arrowInfo.entry.itemId);
        if (invIndex >= 0) {
          const entry = this.inventory[invIndex];
          const currentQty = Math.max(0, toNumber(entry.qty, 0));
          
          if (currentQty > 0) {
            // Consume from inventory (decrement quantity)
            const newQty = Math.max(0, currentQty - 1);
            
            // Track consumption metadata (like potions do)
            ensureInventoryEntryMetadata(entry);
            entry.originalQty = Math.max(0, toNumber(entry.originalQty || currentQty, currentQty) - 1);
            entry.consumedOriginal = (entry.consumedOriginal || 0) + 1;
            incrementConsumptionRecord(this.consumptionTotals, entry.itemId, 'original');
            
            entry.qty = newQty;
            entry.quantity = newQty;
            
            if (newQty <= 0) {
              this.inventory.splice(invIndex, 1);
            }
            
            // Send real-time consumption event via socket (using ammo-consumed, not potion-consumed)
            if (this.heroId && this.scene?.gameManager?.currentBattleId) {
              if (!socketService.isSocketConnected()) {
                console.warn('[Player.consumeArrow] ⚠️ Socket not connected! Arrow consumption will not be tracked in real-time.');
              } else {
                try {
                  socketService.consumeAmmo({
                    heroId: this.heroId,
                    battleId: this.scene.gameManager.currentBattleId,
                    itemId: entry.itemId,
                    itemType: arrowInfo.definition.type || 'arrow',
                    quantity: 1,
                    timestamp: Date.now(),
                    ammoType: 'arrow'
                  });
                } catch (err) {
                  console.error('[Player.consumeArrow] Failed to send arrow consumption via socket:', err);
                }
              }
            }
            
            // After consuming from inventory, check if we should stop shooting
            if (!this.hasArrowItem() && (this.arrowCount || 0) === 0) {
              this.stopShooting('no_arrows');
            }
            
            this.markSidebarResourcesDirty();
            return true;
          }
        }
      }
      // If we have arrow items but couldn't consume, still return true (might be a timing issue)
      return true;
    }
    
    this.stopShooting('no_arrows');
    return false;
  }

  hasArrowItem() {
    return !!this.getBestArrowInventoryEntry();
  }

  getArrowStats() {
    const info = this.getBestArrowInventoryEntry();
    if (!info) return null;
    const range = Number(info.effect?.range);
    const damage = Number(info.effect?.damage);
    const speed = Number(info.effect?.speed);
    return {
      range: Number.isFinite(range) ? range : null,
      damage: Number.isFinite(damage) ? damage : null,
      speed: Number.isFinite(speed) ? speed : null,
      itemId: info.entry?.itemId || null,
    };
  }

  getArrowItemIconKey() {
    const info = this.getBestArrowInventoryEntry();
    if (!info || !info.definition) return null;
    return info.definition.iconKey || null;
  }

  parseItemEffect(effect) {
    if (!effect) return null;
    if (typeof effect === 'object') return effect;
    if (typeof effect === 'string') {
      try {
        return JSON.parse(effect);
      } catch (_) {
        return null;
      }
    }
    return null;
  }

  getBestArrowInventoryEntry() {
    const entries = Array.isArray(this.inventory) ? this.inventory : [];
    if (!entries.length) return null;
    const itemsById = this.scene?.itemsById;
    let best = null;
    entries.forEach((entry) => {
      if (!entry) return;
      const itemId = entry.itemId ?? entry.item_id;
      if (!itemId) return;
      const definition = itemsById && typeof itemsById.get === 'function' ? itemsById.get(itemId) : null;
      const rawEffect = definition?.effect ?? entry.effect ?? entry.effect_json ?? null;
      const parsedEffect = this.parseItemEffect(rawEffect);
      if (!parsedEffect || parsedEffect.range == null) return;
      if (!best || (Number(parsedEffect.range) || 0) > (Number(best.effect?.range) || 0)) {
        best = { entry, definition, effect: parsedEffect };
      }
    });
    return best;
  }

  hasArrows() {
    return (this.arrowCount || 0) > 0 || this.hasArrowItem();
  }

  update(allPlayers, food, worldBounds) {
    if (!this.isAlive) return;
    if (this.commandState?.shootModeActive && !this.hasArrows()) {
      this.stopShooting('no_arrows');
    }

    // Pause during intermission or resume delay
    if (this.scene.intermissionLoading || this.scene.intermissionActive || (this.scene.intermissionResumeDelayMs && this.scene.intermissionResumeDelayMs > 0)) {
      return;
    }

    const deltaMs = (this.scene?.game?.loop?.delta) || 16;
    // Passive resource updates
    this.updateUltimateCharge(deltaMs);
    this.updateResourceRegen(deltaMs);
    this.syncSidebarResources();

    // Periodic item auto-use check (staggered per player)
    try {
      const nowTick = (this.scene.time && typeof this.scene.time.now === 'number') ? this.scene.time.now : Date.now();
      if (this.isAI && nowTick >= (this.nextItemCheckAt || 0)) {
        if (typeof this.tryAutoUseBestItem === 'function') this.tryAutoUseBestItem();
        const usageFactor = this.getItemUsageFactor();
        const minDelay = lerp(5000, 2000, usageFactor);
        const maxDelay = lerp(9000, 4000, usageFactor);
        const range = Math.max(0, maxDelay - minDelay);
        const delay = minDelay + Math.random() * range;
        this.nextItemCheckAt = nowTick + delay;
      }
    } catch (_) {}

    // Check boundary collision
    const bounds = worldBounds || this.scene.gameManager?.worldBounds || null;
    const centerX = bounds ? bounds.centerX : this.scene.game.config.width / 2;
    const centerY = bounds ? bounds.centerY : this.scene.game.config.height / 2;

    if (!this.scene.gameManager.isInsideBoundary(this.sprite.x, this.sprite.y)) {
      // Don't damage players during loot delay period or after match has ended
      if (!this.scene.lootSpecializationStartTime && this.scene.gameState !== 'ended') {
        this.takeDamage(2); // Damage from being outside boundary
      }

      // Don't push back towards center during loot delay period or after match has ended
      if (!this.scene.lootSpecializationStartTime && this.scene.gameState !== 'ended') {
        const angle = Phaser.Math.Angle.Between(this.sprite.x, this.sprite.y, centerX, centerY);
        this.sprite.x += Math.cos(angle) * 3;
        this.sprite.y += Math.sin(angle) * 3;
      }
    }

    // If stunned, don't move or take actions
    if (this.isStunned()) {
      return;
    }

    // AI Movement
    this.aiMovement(allPlayers, food, worldBounds);

    // Check if center command is active - it overrides other movement restrictions
    const now = this.getNow();
    const cs = this.commandState || {};
    const centerActive = cs.centerUntil && now < cs.centerUntil;

    // Don't move if battling, unless running away or center command is active
    if (this.isBattling && !(this.commandState?.runAwayUntil && this.getNow() < this.commandState.runAwayUntil) && !centerActive) {
      return;
    }

    // Don't move if shooting (unless out of stamina or center command is active)
    const isShooting = this.commandState?.shootModeActive;
    if (isShooting && !centerActive) {
      // Check if player has enough stamina to shoot (5 stamina per arrow)
      const arrowStaminaCost = 5;
      const hasStamina = (this.stamina || 0) >= arrowStaminaCost;
      
      if (hasStamina) {
        return; // Stay stationary while shooting
      } else {
        // Out of stamina while shooting - move towards center
        const bounds = worldBounds || this.scene.gameManager?.worldBounds || null;
        if (bounds) {
          this.targetX = bounds.centerX;
          this.targetY = bounds.centerY;
        }
        // Continue to movement code below
      }
    }

    // Move towards target
    const distToTarget = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, this.targetX, this.targetY);
    if (distToTarget > 5) {
      const angle = Phaser.Math.Angle.Between(this.sprite.x, this.sprite.y, this.targetX, this.targetY);
      const speed = this.moveSpeed * 0.016;
      this.sprite.x += Math.cos(angle) * speed;
      this.sprite.y += Math.sin(angle) * speed;
    }
    // Update text and feet positions
    const alivePlayers = allPlayers.filter(p => p.isAlive).length;
    this.updateVisualComponents(alivePlayers);
  }

  aiMovement(allPlayers, food, worldBounds) {
    const now = this.getNow();
    const cs = this.commandState || {};
    
    // If stunned, don't move or take actions
    if (this.isStunned()) {
      return;
    }
    
    const aliveCount = Array.isArray(allPlayers) ? allPlayers.filter(p => p && p.isAlive).length : 0;
    const lateGame = aliveCount > 0 && aliveCount <= 5;
    const recentlyInCombat = this.isBattling || (now - (this.lastDamageEventAt || 0)) < 3000;

    // Center command priority (overrides loot/heal, but check for enemy contact first)
    const centerActive = cs.centerUntil && now < cs.centerUntil;
    if (centerActive && typeof cs.centerTargetX === 'number' && typeof cs.centerTargetY === 'number') {
      // Check if player is in melee range with any enemy - if so, attack instead
      const meleeRange = this.scene?.meleeRange || this.meleeRange || 52;
      let nearestEnemy = null;
      let nearestEnemyDist = Infinity;
      
      allPlayers.forEach(player => {
        if (player !== this && player.isAlive) {
          if (typeof this.canAttack === 'function' && !this.canAttack(player)) return;
          const dist = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, player.sprite.x, player.sprite.y);
          if (dist < nearestEnemyDist) {
            nearestEnemyDist = dist;
            nearestEnemy = player;
          }
        }
      });
      
      // If enemy is in melee range, switch to attack mode
      if (nearestEnemy && nearestEnemyDist < meleeRange) {
        this.targetX = nearestEnemy.sprite.x;
        this.targetY = nearestEnemy.sprite.y;
        // Set prioritize attack to override center command
        this.setPrioritizeAttack(5000); // 5 seconds of attack priority
        return;
      }
      
      // No enemy in range - move towards center
      this.targetX = cs.centerTargetX;
      this.targetY = cs.centerTargetY;
      return; // Override all other priorities
    }

    // Check for loot command priority (before attack)
    const lootActive = cs.prioritizeLootUntil && now < cs.prioritizeLootUntil;
    if (lootActive) {
      const loot = (this.scene.lootSpawner && Array.isArray(this.scene.lootSpawner.loot)) ? this.scene.lootSpawner.loot : [];
      let nearestLoot = null;
      let nearestLootDist = Infinity;
      const osLoot = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;
      const maxLootDistance = 500 * osLoot; // Reasonable search radius
      
      loot.forEach(lootItem => {
        if (!lootItem || !lootItem.lootData) return;
        if (this.scene.gameManager?.isInsideBoundary && !this.scene.gameManager.isInsideBoundary(lootItem.x, lootItem.y)) return;
        const dist = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, lootItem.x, lootItem.y);
        if (dist < nearestLootDist && dist < maxLootDistance) {
          nearestLootDist = dist;
          nearestLoot = lootItem;
        }
      });
      
      if (nearestLoot) {
        this.targetX = nearestLoot.x;
        this.targetY = nearestLoot.y;
        return; // Prioritize loot over everything else
      }
    }

    if (cs.attackTarget && (!cs.attackTarget.isAlive || !cs.attackTarget.sprite)) {
      cs.attackTarget = null;
    }
    const attackActive = cs.prioritizeAttackUntil && now < cs.prioritizeAttackUntil;
    if (attackActive && (!cs.attackTarget || !cs.attackTarget.sprite)) {
      cs.attackTarget = this.findNearestEnemy(allPlayers);
    }
    if (attackActive && cs.attackTarget && cs.attackTarget.sprite) {
      this.targetX = cs.attackTarget.sprite.x;
      this.targetY = cs.attackTarget.sprite.y;
      return;
    }

    const defendActive = cs.defendUntil && now < cs.defendUntil;

    //if the player's strategyLooting is 100 and there are no more enemies, then make a beeline for the nearest loot (not food)
    if (this.strategyLooting === 100 && allPlayers.filter(p => p.isAlive && p !== this && p.team !== this.team).length === 0) {
      let nearestLoot = null;
      let nearestLootDist = Infinity;
      this.scene.lootSpawner.loot.forEach(lootItem => {
        const dist = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, lootItem.x, lootItem.y);
        if (dist < nearestLootDist) { nearestLootDist = dist; nearestLoot = lootItem; }
      });
      if (nearestLoot) {
        this.targetX = nearestLoot.x;
        this.targetY = nearestLoot.y;
        return;
      }
    }

    // Command: run away override (takes top priority while active)
    if (cs.runAwayUntil && now < cs.runAwayUntil) {

      // Move away from nearest enemy if any, else move towards map edge
      let nearestEnemy = null;
      let nearestDistance = Infinity;
      allPlayers.forEach(player => {
        if (player !== this && player.isAlive) {
          const dist = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, player.sprite.x, player.sprite.y);
          if (dist < nearestDistance) {
            nearestDistance = dist;
            nearestEnemy = player;
          }
        }
      });
      const osRun = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;
      if (nearestEnemy) {
        const angleAway = Phaser.Math.Angle.Between(nearestEnemy.sprite.x, nearestEnemy.sprite.y, this.sprite.x, this.sprite.y);
        const runDist = 220 * osRun;
        this.targetX = this.sprite.x + Math.cos(angleAway) * runDist;
        this.targetY = this.sprite.y + Math.sin(angleAway) * runDist;
      } else {
        // No enemy; move towards center of safe boundary (not random edge point which could be in damage zone)
        const centerX = worldBounds.x + worldBounds.width / 2;
        const centerY = worldBounds.y + worldBounds.height / 2;
        // Move towards center but not all the way (stay somewhat away from center)
        this.targetX = Phaser.Math.Linear(this.sprite.x, centerX, 0.3);
        this.targetY = Phaser.Math.Linear(this.sprite.y, centerY, 0.3);
      }
      
      //try { console.log('[AI] run-away active', { player: this.name, until: cs.runAwayUntil }); } catch (_) {}
      
      return;
    }

    // If low on health or command prefers healing, prioritize finding food
    // Apply Hero strategy preference for looting vs healing (more permissive)
    let healthThreshold = this.maxHealth * (0.5 + (this.strategyLooting / 100) * 0.35);
    const preferHealActive = cs.preferHealUntil && now < cs.preferHealUntil;
    const forceFoodActive = cs.forceFoodUntil && now < cs.forceFoodUntil;
    if (forceFoodActive) {
      healthThreshold = Math.max(this.maxHealth * 0.8, healthThreshold);
    }
    if (preferHealActive || forceFoodActive || this.health < healthThreshold) {
      let nearestFood = null;
      let nearestFoodDist = Infinity;
      food.forEach(foodItem => {
        // Only consider food that's inside the safe boundary (not in damage zone)
        if (worldBounds.contains(foodItem.x, foodItem.y) && 
            this.scene.gameManager?.isInsideBoundary && 
            this.scene.gameManager.isInsideBoundary(foodItem.x, foodItem.y)) {
          const dist = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, foodItem.x, foodItem.y);
          if (dist < nearestFoodDist) {
            nearestFoodDist = dist;
            nearestFood = foodItem;
          }
        }
      });

      const os = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;
      // Apply looting strategy preference to food seeking distance (wider radius)
      let foodSeekDistance = 320 * os * (0.7 + (this.strategyLooting / 100) * 1.1);
      if (forceFoodActive) foodSeekDistance *= 2.2;
      if (nearestFood && nearestFoodDist < foodSeekDistance) {
        this.targetX = nearestFood.x;
        this.targetY = nearestFood.y;
        //try { console.log('[AI] seeking food', { player: this.name, x: this.targetX, y: this.targetY }); } catch (_) {}
        return; // Go for food and ignore other logic for now
      }
    }

    // Seek beneficial loot (potions) based on circumstances and strategy
    {
      const osLoot = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;
      const lootSeekBase = 300 * osLoot;
      const loot = (this.scene.lootSpawner && Array.isArray(this.scene.lootSpawner.loot)) ? this.scene.lootSpawner.loot : [];
        const nowBuff = Date.now();
        const hasStrengthBuff = !!(this.commandState?.strengthBuff && nowBuff < this.commandState.strengthBuff.until);
      const inCombat = this.isInCombatOrThreatened ? this.isInCombatOrThreatened(1.3) : false;

      // 1) Healing potions when low health or heal preferred
      if (preferHealActive || forceFoodActive || this.health < this.maxHealth * (0.55 - (this.strategyItemUse / 100) * 0.25)) {
        let nearestHeal = null;
        let nearestHealDist = Infinity;
        loot.forEach(node => {
          const ld = node && node.lootData;
          if (!ld || ld.type !== 'healing') return;
          if (this.scene.gameManager?.isInsideBoundary && !this.scene.gameManager.isInsideBoundary(node.x, node.y)) return;
          const d = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, node.x, node.y);
          if (d < nearestHealDist) { nearestHealDist = d; nearestHeal = node; }
        });
        const seekDist = lootSeekBase * (0.7 + (this.strategyItemUse / 100) * 1.1);
        if (nearestHeal && nearestHealDist < seekDist) {
          this.targetX = nearestHeal.x;
          this.targetY = nearestHeal.y;
          return;
        }
      }

      // 2) Strength potions when in combat and no active buff
      if (inCombat && !hasStrengthBuff && (this.strategyItemUse > 25)) {
        let nearestStr = null;
        let nearestStrDist = Infinity;
        loot.forEach(node => {
          const ld = node && node.lootData;
          if (!ld || ld.type !== 'strength') return;
          if (this.scene.gameManager?.isInsideBoundary && !this.scene.gameManager.isInsideBoundary(node.x, node.y)) return;
          const d = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, node.x, node.y);
          if (d < nearestStrDist) { nearestStrDist = d; nearestStr = node; }
        });
        const seekDist = lootSeekBase * (0.6 + (this.strategyItemUse / 100) * 0.9);
        if (nearestStr && nearestStrDist < seekDist) {
          // Chance based on strategy to avoid too deterministic behavior
          const chance = 0.3 + (this.strategyItemUse / 100) * 0.5; // 30%..80%
          if (Math.random() < chance) {
            this.targetX = nearestStr.x;
            this.targetY = nearestStr.y;
            return;
          }
        }
      }
    }

    // Opportunistic snack: even if we're pursuing another target, grab nearby food
    // when not at full health and a pickup is very close
    {
      const osSnack = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;
      const snackRadius = 220 * osSnack * (cs.forceFoodUntil && now < cs.forceFoodUntil ? 1.8 : 1); // tight radius for opportunistic grab
      if (this.health < this.maxHealth) {
        let snack = null;
        let snackDist = Infinity;
        food.forEach(f => {
          if (this.scene.gameManager?.isInsideBoundary ? this.scene.gameManager.isInsideBoundary(f.x, f.y) : true) {
            const d = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, f.x, f.y);
            if (d < snackRadius && d < snackDist) { snack = f; snackDist = d; }
          }
        });
        if (snack) {
          this.targetX = snack.x;
          this.targetY = snack.y;
          return;
        }
      }
    }

    // Opportunistic loot: nearby potion grab if beneficial
    {
      const osSnack = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;
      const snackRadius = 220 * osSnack;
      const loot = (this.scene.lootSpawner && Array.isArray(this.scene.lootSpawner.loot)) ? this.scene.lootSpawner.loot : [];
      let best = null;
      let bestDist = Infinity;
      loot.forEach(node => {
        const ld = node && node.lootData;
        if (!ld) return;
        if (this.scene.gameManager?.isInsideBoundary && !this.scene.gameManager.isInsideBoundary(node.x, node.y)) return;
        // Heuristic: favor healing if not full health, else strength in combat, else any
        if (ld.type === 'healing' && this.health >= this.maxHealth) return;
        const d = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, node.x, node.y);
        if (d < snackRadius && d < bestDist) { best = node; bestDist = d; }
      });
      if (best) {
        this.targetX = best.x;
        this.targetY = best.y;
        return;
      }
    }

    // Use game mode's target selection if available, otherwise default AI
    let nearestEnemy = null;
    let nearestDistance = Infinity;
    
    if (this.scene.gameModeManager) {
      nearestEnemy = this.scene.gameModeManager.getPlayerTarget(this, allPlayers);
      if (nearestEnemy) {
        nearestDistance = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, nearestEnemy.sprite.x, nearestEnemy.sprite.y);
      }
      // Note: If game mode returns null, player keeps current target (e.g., charging to a position)
    } else {
      // Fallback: Find nearest enemy (default behavior)
      allPlayers.forEach(player => {
        if (player !== this && player.isAlive) {
          const dist = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, player.sprite.x, player.sprite.y);
          if (dist < nearestDistance) {
            nearestDistance = dist;
            nearestEnemy = player;
          }
        }
      });
    }

    const os2 = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;
    // Apply aggression strategy preference to engagement distance
    const engagementDistance = 500 * os2 * (0.5 + (this.strategyAggression / 100) * 1.0); // scales with aggression
    const aggressionFactor = this.strategyAggression / 100; // 0..1
    // Highly aggressive heroes will pursue the nearest enemy even beyond the nominal engagement distance
    if (nearestEnemy && (nearestDistance < engagementDistance || aggressionFactor >= 0.85 || lateGame || recentlyInCombat)) {
      // Attack preference: move towards enemy more aggressively
      if (cs.prioritizeAttackUntil && now < cs.prioritizeAttackUntil) {
        const closer = 1.0; // no change for now, but can bias
        this.targetX = nearestEnemy.sprite.x + (Math.random() - 0.5) * closer;
        this.targetY = nearestEnemy.sprite.y + (Math.random() - 0.5) * closer;
        //try { console.log('[AI] prioritize attack', { player: this.name, enemy: nearestEnemy.name }); } catch (_) {}
      } else {
        // Apply aggression strategy to pursuit behavior
        if (aggressionFactor > 0.7) {
          // High aggression: direct pursuit
          this.targetX = nearestEnemy.sprite.x;
          this.targetY = nearestEnemy.sprite.y;
        } else if (aggressionFactor < 0.3) {
          // Low aggression: defensive positioning
          const angleAway = Phaser.Math.Angle.Between(nearestEnemy.sprite.x, nearestEnemy.sprite.y, this.sprite.x, this.sprite.y);
          const defensiveDist = 100 * os2;
          this.targetX = this.sprite.x + Math.cos(angleAway) * defensiveDist;
          this.targetY = this.sprite.y + Math.sin(angleAway) * defensiveDist;
        } else {
          // Medium aggression: default pursue
          this.targetX = nearestEnemy.sprite.x;
          this.targetY = nearestEnemy.sprite.y;
        }
      }
    } else {
      // No nearby enemy or enemy too far away
      // Check if we need to set a new target
      
      // Calculate distance to current target
      const distToCurrentTarget = Phaser.Math.Distance.Between(
        this.sprite.x, 
        this.sprite.y, 
        this.targetX || this.sprite.x, 
        this.targetY || this.sprite.y
      );
      
      // If we're close to our target or don't have one, pick a new destination
      if (!this.targetX || !this.targetY || distToCurrentTarget < 50) {
        // For team battles, maintain charge behavior if not yet engaged
        if (this.teamBattleState && !this.teamBattleState.hasEngaged && this.teamBattleState.chargeTarget) {
          // Keep existing charge target
          return;
        }
        
        // Apply positioning strategy preference for wandering behavior
        const positioningFactor = this.strategyPositioning / 100; // 0 to 1
        const centerX = worldBounds.x + worldBounds.width / 2;
        const centerY = worldBounds.y + worldBounds.height / 2;

        if ((lateGame || recentlyInCombat) && nearestEnemy?.sprite) {
          this.targetX = nearestEnemy.sprite.x;
          this.targetY = nearestEnemy.sprite.y;
        } else if (aliveCount > 0 && aliveCount <= 8) {
          // Late game: bias strongly toward center to avoid fog chases
          this.targetX = centerX;
          this.targetY = centerY;
        } else if (positioningFactor > 0.7) {
          // High positioning preference: stay near edges
          const edgeX = Math.random() < 0.5 ? worldBounds.x + 100 : worldBounds.right - 100;
          const edgeY = Math.random() < 0.5 ? worldBounds.y + 100 : worldBounds.bottom - 100;
          this.targetX = edgeX;
          this.targetY = edgeY;
        } else if (positioningFactor < 0.3) {
          // Low positioning preference: charge center
          this.targetX = centerX;
          this.targetY = centerY;
        } else {
          // Medium positioning preference: neutral ring wander around center (less center bias)
          const angle = Math.random() * Math.PI * 2;
          const radius = Phaser.Math.Between(400, 900) * os2; // widen/speed by tuning
          this.targetX = centerX + Math.cos(angle) * radius;
          this.targetY = centerY + Math.sin(angle) * radius;
        }
        //try { console.log('[AI] wandering based on strategy', { player: this.name, x: this.targetX, y: this.targetY, positioning: this.strategyPositioning }); } catch (_) {}
      }
    }

    // Safety: avoid selecting targets outside the shrinking boundary; nudge inward
    // When health is low (below 30%), be more aggressive about staying inside safe zone
    if (this.scene?.gameManager?.isInsideBoundary) {
      const tx = this.targetX || this.sprite.x;
      const ty = this.targetY || this.sprite.y;
      const inside = this.scene.gameManager.isInsideBoundary(tx, ty);
      if (!inside) {
        const centerX = worldBounds.x + worldBounds.width / 2;
        const centerY = worldBounds.y + worldBounds.height / 2;
        // If health is low, move more aggressively towards center (70% of the way)
        // Otherwise, nudge half-way towards center
        const healthPercent = this.maxHealth > 0 ? this.health / this.maxHealth : 1;
        const nudgeAmount = healthPercent < 0.3 ? 0.7 : 0.5;
        this.targetX = Phaser.Math.Linear(this.sprite.x, centerX, nudgeAmount);
        this.targetY = Phaser.Math.Linear(this.sprite.y, centerY, nudgeAmount);
      }
    }
  }

  setPreferHeal(durationMs, options = {}) {
    const now = this.getNow();
    const duration = Math.max(1000, durationMs || CommandDurations.healBiasMs);
    this.commandState = this.commandState || {};
    this.commandState.preferHealUntil = Math.max(this.commandState.preferHealUntil || 0, now + duration);
    if (options.force) {
      this.commandState.forceFoodUntil = Math.max(this.commandState.forceFoodUntil || 0, now + duration);
    }
    try { console.log('[ChatCmd] preferHealUntil', { player: this.name, until: this.commandState.preferHealUntil, forced: !!options.force }); } catch (_) {}
  }

  setPrioritizeAttack(durationMs) {
    const now = this.getNow();
    const duration = Math.max(1000, durationMs || CommandDurations.attackBiasMs);
    this.commandState = this.commandState || {};
    this.commandState.prioritizeAttackUntil = Math.max(this.commandState.prioritizeAttackUntil || 0, now + duration);
    this.commandState.prioritizeAttackTriggeredAt = now;
    this.applySpeedModifier('attackBoost', 2.5, duration);
    this.commandState.attackTarget = this.findNearestEnemy();
    try { console.log('[ChatCmd] prioritizeAttackUntil', { player: this.name, until: this.commandState.prioritizeAttackUntil }); } catch (_) {}
  }

  setRunAway(durationMs, cooldownMs = CommandDurations.runCooldownMs) {
    const now = this.getNow();
    const duration = Math.max(1500, durationMs || CommandDurations.runDurationMs);
    const cooldown = Math.max(1000, cooldownMs || CommandDurations.runCooldownMs);

    this.commandState = this.commandState || {};
    const last = this.commandState.lastRunAt || 0;
    if (now - last < cooldown) {
      try { console.log('[ChatCmd] run cooldown active', { player: this.name, msRemaining: Math.max(0, cooldown - (now - last)) }); } catch (_) {}
      return false;
    }

    this.commandState.lastRunAt = now;
    this.commandState.runAwayUntil = Math.max(this.commandState.runAwayUntil || 0, now + duration);
    
    // Set 3 seconds of invulnerability when running
    this.invulnerableUntil = now + 3000;
    
    this.applySpeedModifier('runAway', 2, duration);
    try { console.log('[ChatCmd] runAwayUntil', { player: this.name, until: this.commandState.runAwayUntil, invulnerableUntil: this.invulnerableUntil }); } catch (_) {}
    return true;
  }

  takeDamage(amount, options = {}) {
    if (!this.isAlive) return 0;
    
    // Check if player is currently invulnerable (e.g., from run command)
    const now = this.getNow();
    if (this.invulnerableUntil && now < this.invulnerableUntil) {
      // Player is invulnerable - no damage taken, but still return 0 to indicate no damage
      return 0;
    }
    
    const reducedDamage = this.calculateDamageReduction(amount);
    this.health -= reducedDamage;
    this.damageTaken += reducedDamage;
    this.addUltimateChargeFromDamage(reducedDamage, 'taken');
    if (typeof this.markCombatEngaged === 'function') {
      this.markCombatEngaged('damage_taken');
    }
    this.updateHealthBar();
    if (this.isAI) {
      const soon = now + 250;
      this.nextItemCheckAt = this.nextItemCheckAt == null ? soon : Math.min(this.nextItemCheckAt, soon);
    }
    try {
      this.showDamageNumber(reducedDamage, { critical: !!options.critical });
    } catch (_) {}
    if (this.health <= 0) {
      this.die();
    }
    return reducedDamage; // Return actual damage taken
  }

  addElimination() {
    this.eliminations++;
  }

  heal(amount, options = {}) {
    if (!this.isAlive || !Number.isFinite(amount) || amount <= 0) return 0;

    const missing = Math.max(0, (this.maxHealth || 0) - (this.health || 0));
    const actualHeal = Math.min(missing, amount);
    if (actualHeal <= 0) {
      return 0;
    }
    this.health = Math.min(this.maxHealth || 0, (this.health || 0) + actualHeal);
    this.updateHealthBar();
    if (!options?.silent && actualHeal > 0) {
      try {
        this.showDamageNumber(actualHeal, { heal: true });
      } catch (_) {}
    }
    return actualHeal;
  }

  setHealthToOne() {
    if (!this.isAlive) return;
    this.health = Math.max(1, Math.min(this.health, this.maxHealth || 1));
    this.updateHealthBar();
    if (this.scene?.showEmote) {
      this.scene.showEmote(this, '⚪');
    }
  }

  restoreStamina(amount = 0) {
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    const max = Math.max(0, this.maxStamina || 0);
    if (max <= 0) return 0;
    const current = Math.max(0, this.stamina || 0);
    const actual = Math.min(amount, max - current);
    if (actual <= 0) return 0;
    this.stamina = current + actual;
    this.markSidebarResourcesDirty();
    return actual;
  }

  restoreMana(amount = 0) {
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    const max = Math.max(0, this.maxMana || 0);
    if (max <= 0) return 0;
    const current = Math.max(0, this.mana || 0);
    const actual = Math.min(amount, max - current);
    if (actual <= 0) return 0;
    this.mana = current + actual;
    this.markSidebarResourcesDirty();
    return actual;
  }

  spendStamina(amount = 0) {
    if (!Number.isFinite(amount) || amount <= 0) return true;
    if ((this.maxStamina || 0) <= 0) return true;
    const current = Math.max(0, this.stamina || 0);
    const next = Math.max(0, current - amount);
    this.stamina = next;
    this.markSidebarResourcesDirty();
    return current >= amount;
  }

  applyAttackStaminaCost(cost = 2) {
    if ((this.maxStamina || 0) <= 0) return true;
    let finalCost = cost;
    const now = this.getNow();
    if (this.commandState?.rageUntil && now < this.commandState.rageUntil) {
      finalCost *= 3;
    }
    this.spendStamina(finalCost);
    return this.stamina > 0;
  }

  isDefending() {
    const now = this.getNow();
    return !!(this.commandState?.defendUntil && now < this.commandState.defendUntil);
  }

  markCombatEngaged(reason = 'combat') {
    const now = this.getNow();
    this.inCombatUntil = now + this.combatStateGraceMs;
    this.lastDamageEventAt = now;
  }

  isInCombat() {
    if (!this.inCombatUntil) return false;
    return this.getNow() < this.inCombatUntil;
  }

  markSidebarResourcesDirty() {
    this._sidebarResourceDirty = true;
  }

  getStaminaRatio() {
    const max = Math.max(0, this.maxStamina || 0);
    if (!Number.isFinite(max) || max <= 0) return 1;
    const current = Math.max(0, this.stamina || 0);
    return Math.max(0, Math.min(1, current / max));
  }

  getFatiguePenaltyScale() {
    const ratio = this.getStaminaRatio();
    if (ratio > 0.2) return 0;
    const severity = (0.2 - ratio) / 0.2;
    return Math.max(0, Math.min(1, severity));
  }

  getFatigueDamageMultiplier() {
    const severity = this.getFatiguePenaltyScale();
    if (severity <= 0) return 1;
    return 1 + severity * 1.0; // up to 2x damage taken when stamina is 0
  }

  applyHealLockout(durationMs = 1000) {
    const now = this.getNow();
    const lockoutUntil = now + Math.max(0, durationMs || 0);
    this.ultHealLockoutUntil = Math.max(this.ultHealLockoutUntil || 0, lockoutUntil);
  }

  syncSidebarResources(force = false) {
    if (!this.scene?.refreshSidebarForPlayer) return;
    if (!force && !this._sidebarResourceDirty) return;
    const now = this.getNow();
    if (!force && now - (this._lastSidebarSyncAt || 0) < 150) {
      return;
    }
    this._lastSidebarSyncAt = now;
    this._sidebarResourceDirty = false;
    try { this.scene.refreshSidebarForPlayer(this); } catch (_) {}
  }

  updateResourceRegen(deltaMs = 16) {
    if (!this.isAlive) return;
    const deltaSeconds = deltaMs / 1000;
    if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) return;

    // Health regen (silent)
    if ((this.maxHealth || 0) > 0 && (this.health || 0) < (this.maxHealth || 0)) {
      const regen = (this.maxHealth || 0) * (this.healthRegenRate || 0) * deltaSeconds;
      if (regen > 0) {
        const gained = this.heal(regen, { silent: true });
        if (gained > 0) {
          // No sidebar update needed; health bar handled elsewhere
        }
      }
    }

    // Mana regen
    if ((this.maxMana || 0) > 0 && (this.mana || 0) < (this.maxMana || 0)) {
      const regen = (this.maxMana || 0) * (this.manaRegenRate || 0) * deltaSeconds;
      if (regen > 0) {
        const gained = this.restoreMana(regen);
        if (gained > 0) {
          this.markSidebarResourcesDirty();
        }
      }
    }

    // Stamina regen (only if out of combat unless defending)
    const canRegenStamina = this.isDefending() || !this.isInCombat();
    if (canRegenStamina && (this.maxStamina || 0) > 0 && (this.stamina || 0) < (this.maxStamina || 0)) {
      const regen = (this.maxStamina || 0) * (this.staminaRegenRate || 0) * deltaSeconds;
      if (regen > 0) {
        const gained = this.restoreStamina(regen);
        if (gained > 0) {
          this.markSidebarResourcesDirty();
        }
      }
    }
  }

  // --- Ultimate System ---
  initializeUltimate() {
    const types = ['damage_burst', 'area_burst'];
    this.ultimateType = types[Math.floor(Math.random() * types.length)];
    this.ultimateCharge = 0;
    this.lastUltimateUpdateAt = this.getNow();
  }

  getUltimateChargeMultiplier() {
    const effectiveSelf = this.getEffectiveSelf ? this.getEffectiveSelf() : (this.self || 10);
    const clamped = Math.max(10, Math.min(190, effectiveSelf));
    const bonus = ((clamped - 10) / 180) * 0.1; // up to +20%
    return 1 + bonus;
  }

  updateUltimateCharge(deltaMs = 16) {
    if (!this.isAlive || !this.ultimateType) return;
    const now = this.getNow();
    const elapsedSeconds = (now - this.lastUltimateUpdateAt) / 1000;
    if (elapsedSeconds <= 0) return;
    // Charge 1% per second baseline, scaled by self attribute multiplier
    const passiveGain = elapsedSeconds * 0.25 * this.getUltimateChargeMultiplier();
    this.addUltimateCharge(passiveGain, 'passive');
    this.lastUltimateUpdateAt = now;
  }

  addUltimateCharge(percent, source = 'unknown') {
    if (!this.ultimateType || !Number.isFinite(percent) || percent <= 0) return;
    this.ultimateCharge = Math.min(100, this.ultimateCharge + percent);
    if (this.scene?.refreshSidebarForPlayer) {
      try { this.scene.refreshSidebarForPlayer(this); } catch (_) {}
    }
  }

  addUltimateChargeFromDamage(amount, source = 'damage') {
    if (!this.ultimateType || !this.isAlive) return;
    if (!Number.isFinite(amount) || amount <= 0) return;
    const eventGain = 0.2 * this.getUltimateChargeMultiplier(); // 0.5% per event baseline
    this.addUltimateCharge(eventGain, source);
  }

  handleDamageDealtForUltimate(amount) {
    this.addUltimateChargeFromDamage(amount, 'dealt');
    if (typeof this.markCombatEngaged === 'function') {
      this.markCombatEngaged('damage_dealt');
    }
  }

  canUseUltimate() {
    return this.isAlive && this.ultimateType && this.ultimateCharge >= 100;
  }

  useUltimate() {
    if (!this.canUseUltimate()) {
      return false;
    }

    //this.ultimateType = 'damage_burst';
    const type = this.ultimateType;
    this.ultimateCharge = 0;
    this.lastUltimateUpdateAt = this.getNow();
    
    // Start wind-up period (1.5 seconds)
    const windUpDuration = 3000; // 1.5 seconds
    this.commandState = this.commandState || {};
    this.commandState.ultimateWindUpUntil = this.getNow() + windUpDuration;
    this.commandState.ultimateType = type;
    
    // For damage_burst, lock the target during wind-up
    if (type === 'damage_burst') {
      const target = this.findNearestEnemy(this.scene?.players || []);
      if (target) {
        this.commandState.ultimateTarget = target;
      } else {
        // No target found, cancel ultimate
        this.ultimateCharge = 100;
        return false;
      }
    }
    
    // Show visual warnings immediately
    this.showUltimateWindUp(type);
    
    // Schedule actual execution after wind-up
    if (this.scene && this.scene.time) {
      this.scene.time.delayedCall(windUpDuration, () => {
        if (this.isAlive && this.commandState?.ultimateWindUpUntil) {
          this.executeUltimate(type);
        } else {
          // Player died or ultimate was cancelled, clean up
          this.cleanupUltimateWindUp();
          if (this.isAlive) {
            this.ultimateCharge = 100; // Refund if cancelled
          }
        }
      });
    } else {
      // Fallback if scene.time not available
      setTimeout(() => {
        if (this.isAlive && this.commandState?.ultimateWindUpUntil) {
          this.executeUltimate(type);
        } else {
          this.cleanupUltimateWindUp();
          if (this.isAlive) {
            this.ultimateCharge = 100;
          }
        }
      }, windUpDuration);
    }

    return true;
  }

  executeDamageBurstUltimate(lockedTarget = null) {
    // Use locked target from wind-up if provided, otherwise find nearest
    const target = lockedTarget || this.findNearestEnemy(this.scene?.players || []);
    if (!target) return false;

    const baseDamage = this.calculateMeleeDamage();
    // Reduce ultimate damage when stats are normalized (to balance with lower base damage)
    const isNormalized = this.statsNormalized === true;
    const multiplier = isNormalized 
      ? 8 + Math.random() * 6  // 8x - 14x damage when normalized (reduced from 16x-28x)
      : 16 + Math.random() * 12; // 16x - 28x damage when not normalized
    const baseUltimateDamage = isNormalized ? 175 : 350; // Reduced base damage when normalized
    let ultimateDamage = baseUltimateDamage + baseDamage * multiplier;
    
    // Cap damage for hero players to leave them with at least 10 health
    // If they're already at 10 or below, prevent any further damage
    const isHeroPlayer = !target.isAI;
    if (isHeroPlayer) {
      if (target.health <= 10) {
        // Hero player already at or below 10 health - prevent ultimate from killing them
        ultimateDamage = 0;
      } else {
        // Cap damage to leave hero player with exactly 10 health
        const maxAllowedDamage = target.health - 10;
        if (ultimateDamage > maxAllowedDamage) {
          ultimateDamage = maxAllowedDamage;
        }
      }
    }
    
    const actualDamage = target.takeDamage(ultimateDamage, { critical: true });
    this.damageDealt += actualDamage;
    if (typeof this.handleDamageDealtForUltimate === 'function') {
      //this.handleDamageDealtForUltimate(actualDamage);
    }

    if (this.scene?.createUltimateParticles) {
      this.scene.createUltimateParticles('damage_burst', target.sprite?.x || this.sprite.x, target.sprite?.y || this.sprite.y);
    }

    if (typeof target.applyHealLockout === 'function') {
      target.applyHealLockout(1000);
    }

    // Apply stun effect (3 seconds base, reduced by Self attribute, minimum 1 second)
    if (typeof target.applyStun === 'function') {
      target.applyStun(3000);
    }

    if (!target.isAlive) {
      this.addElimination();
      if (this.scene?.createDeathMarker && target.sprite) {
        this.scene.createDeathMarker(target.sprite.x, target.sprite.y, { popScale: target.sprite.scale * 10 });
      }
    }
    return true;
  }

  executeAreaBurstUltimate() {
    const players = this.scene?.players || [];
    const px = this.sprite?.x || 0;
    const py = this.sprite?.y || 0;
    const radius = 520;
    const targets = players.filter(p => p && p !== this && p.isAlive && p.sprite && (!this.isTeammate || !this.isTeammate(p)) &&
      Phaser.Math.Distance.Between(px, py, p.sprite.x, p.sprite.y) <= radius);

    if (!targets.length) return false;

    const baseDamage = this.calculateMeleeDamage();
    // Reduce ultimate damage when stats are normalized (to balance with lower base damage)
    const isNormalized = this.statsNormalized === true;
    targets.forEach(target => {
      const multiplier = isNormalized
        ? 4 + Math.random() * 4  // 4x - 8x damage when normalized (reduced from 8x-16x)
        : 8 + Math.random() * 8; // 8x - 16x damage when not normalized
      const baseUltimateDamage = isNormalized ? 60 : 120; // Reduced base damage when normalized
      let ultimateDamage = baseUltimateDamage + baseDamage * multiplier;
      
      // Cap damage for hero players to leave them with at least 10 health
      // If they're already at 10 or below, prevent any further damage
      const isHeroPlayer = !target.isAI;
      if (isHeroPlayer) {
        if (target.health <= 10) {
          // Hero player already at or below 10 health - prevent ultimate from killing them
          ultimateDamage = 0;
        } else {
          // Cap damage to leave hero player with exactly 10 health
          const maxAllowedDamage = target.health - 10;
          if (ultimateDamage > maxAllowedDamage) {
            ultimateDamage = maxAllowedDamage;
          }
        }
      }
      
      const actualDamage = target.takeDamage(ultimateDamage);
      this.damageDealt += actualDamage;
      if (typeof this.handleDamageDealtForUltimate === 'function') {
        //this.handleDamageDealtForUltimate(actualDamage);
      }
      if (!target.isAlive) {
        this.addElimination();
        if (this.scene?.createDeathMarker) {
          this.scene.createDeathMarker(target.sprite.x, target.sprite.y, { popScale: target.sprite.scale * 10 });
        }
      }
      if (typeof target.applyHealLockout === 'function') {
        target.applyHealLockout(1000);
      }
      
      // Apply stun effect (3 seconds base, reduced by Self attribute, minimum 1 second)
      if (typeof target.applyStun === 'function') {
        target.applyStun(3000);
      }
    });

    if (this.scene?.createUltimateParticles) {
      this.scene.createUltimateParticles('area_burst', px, py);
    }
    return true;
  }

  showUltimateWindUp(type) {
    if (!this.scene || !this.sprite) return;
    
    // 1. Player glow effect
    const glowColor = type === 'damage_burst' ? 0xFFD700 : 0xFF4500; // Gold or Orange-red
    const objectScale = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;
    const glowSize = 60 * objectScale;
    
    this.ultimateGlow = this.scene.add.circle(
      this.sprite.x, 
      this.sprite.y, 
      glowSize, 
      glowColor, 
      0.6
    );
    this.ultimateGlow.setDepth(this.sprite.depth - 1);
    if (this.scene.uiCamera?.ignore) {
      this.scene.uiCamera.ignore(this.ultimateGlow);
    }
    
    // Pulse animation
    this.scene.tweens.add({
      targets: this.ultimateGlow,
      scale: { from: 0.8, to: 1.2 },
      alpha: { from: 0.4, to: 0.8 },
      duration: 500,
      yoyo: true,
      repeat: -1
    });
    
    // Update glow position in updateVisualComponents
    this.ultimateGlowUpdate = true;
    
    // 2. Emote/text above player
    if (this.scene?.showEmote) {
      const icon = type === 'damage_burst' ? '⚔️' : '💥';
      this.scene.showEmote(this, icon);
    }
    
    // 3. Target/Area indicators
    if (type === 'damage_burst') {
      const target = this.commandState?.ultimateTarget;
      if (target && target.sprite) {
        this.showTargetingLine(target);
      }
    } else if (type === 'area_burst') {
      // Show area indicator at player position
      this.showAreaIndicator(this.sprite.x, this.sprite.y, 520);
    }
  }

  showTargetingLine(target) {
    if (!this.scene || !this.sprite || !target.sprite) return;
    
    const graphics = this.scene.add.graphics();
    graphics.lineStyle(3, 0xFFD700, 0.8);
    graphics.setDepth(5000);
    if (this.scene.uiCamera?.ignore) {
      this.scene.uiCamera.ignore(graphics);
    }
    
    // Store reference for cleanup
    this.ultimateTargetingLine = graphics;
    this.ultimateTargetingTarget = target;
    
    // Update line position continuously during wind-up
    const updateLine = () => {
      if (!this.ultimateTargetingLine || !this.isAlive || !target.isAlive || !this.sprite || !target.sprite) {
        return;
      }
      
      graphics.clear();
      graphics.lineStyle(3, 0xFFD700, 0.8);
      graphics.lineBetween(
        this.sprite.x, 
        this.sprite.y, 
        target.sprite.x, 
        target.sprite.y
      );
      
      // Continue updating if still in wind-up
      if (this.commandState?.ultimateWindUpUntil && this.getNow() < this.commandState.ultimateWindUpUntil) {
        if (this.scene && this.scene.time) {
          this.scene.time.delayedCall(50, updateLine);
        } else {
          setTimeout(updateLine, 50);
        }
      }
    };
    
    // Start updating
    updateLine();
    
    // Pulse animation
    this.scene.tweens.add({
      targets: graphics,
      alpha: { from: 0.3, to: 1 },
      duration: 150,
      yoyo: true,
      repeat: -1
    });
  }

  showAreaIndicator(x, y, radius) {
    if (!this.scene) return;
    
    const circle = this.scene.add.circle(x, y, radius, 0xFF4500, 0.2);
    circle.setStrokeStyle(4, 0xFF4500, 0.8);
    circle.setDepth(5000);
    if (this.scene.uiCamera?.ignore) {
      this.scene.uiCamera.ignore(circle);
    }
    
    // Store reference for cleanup
    this.ultimateAreaIndicator = circle;
    
    // Pulse animation
    this.scene.tweens.add({
      targets: circle,
      scale: { from: 0.9, to: 1.1 },
      alpha: { from: 0.1, to: 0.3 },
      duration: 200,
      yoyo: true,
      repeat: -1
    });
  }

  executeUltimate(type) {
    let executed = false;
    
    // For damage_burst, use the locked target from wind-up
    if (type === 'damage_burst') {
      const lockedTarget = this.commandState?.ultimateTarget;
      if (lockedTarget && lockedTarget.isAlive) {
        // Use the locked target from wind-up
        executed = this.executeDamageBurstUltimate(lockedTarget);
      } else {
        // Target died or is invalid, try to find a new one
        executed = this.executeDamageBurstUltimate();
      }
    } else {
      executed = this.executeAreaBurstUltimate();
    }
    
    // Clean up wind-up visuals
    this.cleanupUltimateWindUp();
    
    if (!executed) {
      this.ultimateCharge = 100; // Refund if failed
      return false;
    }
    
    return true;
  }

  cleanupUltimateWindUp() {
    // Clean up glow
    if (this.ultimateGlow) {
      if (this.scene && this.scene.tweens) {
        this.scene.tweens.killTweensOf(this.ultimateGlow);
      }
      try {
        this.ultimateGlow.destroy();
      } catch (_) {}
      this.ultimateGlow = null;
      this.ultimateGlowUpdate = false;
    }
    
    // Clean up targeting line
    if (this.ultimateTargetingLine) {
      if (this.scene && this.scene.tweens) {
        this.scene.tweens.killTweensOf(this.ultimateTargetingLine);
      }
      try {
        this.ultimateTargetingLine.destroy();
      } catch (_) {}
      this.ultimateTargetingLine = null;
      this.ultimateTargetingTarget = null;
    }
    
    // Clean up area indicator
    if (this.ultimateAreaIndicator) {
      if (this.scene && this.scene.tweens) {
        this.scene.tweens.killTweensOf(this.ultimateAreaIndicator);
      }
      try {
        this.ultimateAreaIndicator.destroy();
      } catch (_) {}
      this.ultimateAreaIndicator = null;
    }
    
    // Clear wind-up state
    if (this.commandState) {
      this.commandState.ultimateWindUpUntil = null;
      this.commandState.ultimateTarget = null;
      this.commandState.ultimateType = null;
    }
  }

  hurt(amount) {
    if (!this.isAlive) return;
    this.health = Math.max(0, this.health - amount);
    this.updateHealthBar();
  }

  die() {
      //console.log('💀 Player.die() called for:', this.name, 'health:', this.health, 'maxHealth:', this.maxHealth);

      // Ensure components are removed so no leftovers remain
      const dx = this.sprite ? this.sprite.x : 0;
      const dy = this.sprite ? this.sprite.y : 0;
      const popScale = this.sprite ? this.sprite.scale * 1.5 : 1.5;

      this.isAlive = false;
      this.invulnerableUntil = 0; // Clear invulnerability on death
      this.ultimateCharge = 0;

      // Track death order/timestamp so rankings can reflect survivor order
      const now = Date.now();
      this.deathTimestamp = now;

      if (this.scene) {
        if (typeof this.scene.deathOrderCounter !== 'number') {
          this.scene.deathOrderCounter = 0;
        }
        this.scene.deathOrderCounter += 1;
        this.deathOrder = this.scene.deathOrderCounter;

        const matchStartTime = this.scene.matchStartTime;
        if (typeof matchStartTime === 'number' && matchStartTime > 0) {
          const aliveSeconds = Math.max(0, (now - matchStartTime) / 1000);
          this.timeAliveSeconds = Math.round(aliveSeconds * 100) / 100;
        }
      } else {
        this.deathOrder = (this.deathOrder || 0) + 1;
      }
      if (this.timeAliveSeconds == null) {
        this.timeAliveSeconds = 0;
      }

      this.clearPendingCommandTimeouts();

      try { if (this.nameText) this.nameText.destroy(); } catch (_) {}
      try { if (this.healthBar) this.healthBar.destroy(); } catch (_) {}
      try { if (this.healthBarBg) this.healthBarBg.destroy(); } catch (_) {}
      try { if (this.shieldSprite) this.shieldSprite.destroy(); } catch (_) {}
      try { if (this.ultimateIcon) this.ultimateIcon.destroy(); } catch (_) {}
      // Clean up ultimate wind-up visuals
      this.cleanupUltimateWindUp();
      // Drop inventory to the ground before destroying sprite
      // Joined players drop fewer items (15% chance) vs AI players (25% chance)
      try {
        if (this.scene && this.scene.lootSpawner && this.sprite) {
          // Joined players keep more of their loot - lower drop rate
          const joinedRate = this.scene?.sessionConfig?.joinedDeathDropRate;
          const dropProbability = (this.heroId && this.hero?.isJoined)
            ? (Number.isFinite(joinedRate) ? joinedRate : 0.25)
            : 0.55;
          this.scene.lootSpawner.dropInventoryWithChanceAt(this, this.sprite.x, this.sprite.y, dropProbability);
        }
      } catch (_) {}

      // Spawn a burst of tokens on death
      try {
        if (this.scene && this.scene.lootSpawner && this.sprite) {
          //vary the number of tokens based on player's level
          const level = this.hero?.level || 1;
          const tokenCount = 5 + Math.max(1, Math.floor(level / 2));
          this.scene.lootSpawner.spawnTokensBurstAt(this.sprite.x, this.sprite.y, tokenCount);
        }
      } catch (_) {}

      // Clear per-player timers (no longer used; kept as safety)
      try { if (this._autoUseTimer) { this._autoUseTimer.remove(false); this._autoUseTimer = null; } } catch (_) {}

      try { if (this.sprite) this.sprite.destroy(); } catch (_) {}
      try { this.hideTaunt(); } catch (_) {}

      // Trigger death marker + quick skull pop via scene helper
      if (this.scene && this.scene.createDeathMarker) {
        this.scene.createDeathMarker(dx, dy, { popScale });
      }
  }

  // Use one consumable if strategy and conditions allow; returns true if used
  tryUseConsumable() {
    if (!this.isAlive) return false;
    const itemsById = this.scene?.itemsById;
    if (!itemsById || typeof itemsById.get !== 'function') return false;
    // Health-based desire: lower health increases chance
    const healthPct = Math.max(0, Math.min(1, this.health / (this.maxHealth || 1)));
    const desire = (this.strategyItemUse / 100) * (1 - healthPct); // high when low hp and liberal
    if (Math.random() > desire) return false;

    // Prefer healing > strength > mana (for now); poison not auto-used
    // Check loot first, then inventory
    const priority = ['healing', 'strength', 'mana'];
    for (const type of priority) {
      // First check loot
      if (Array.isArray(this.loot)) {
        const lootEntry = this.loot.find(entry => {
          const def = itemsById.get(entry.itemId);
          return def && def.type === type && (entry.qty || 0) > 0;
        });
        if (lootEntry) {
          const def = itemsById.get(lootEntry.itemId);
          const ok = this.consumeItem(def);
          if (ok) return true;
        }
      }
      
      // Then check inventory
      const idx = this.inventory.findIndex(entry => {
        const def = itemsById.get(entry.itemId);
        return def && def.type === type && (entry.qty || 0) > 0;
      });
      if (idx >= 0) {
        const entry = this.inventory[idx];
        const def = itemsById.get(entry.itemId);
        const ok = this.consumeItem(def, idx);
        if (ok) return true;
      }
    }
    return false;
  }

  // Consume one item by definition; returns true if applied
  consumeItem(itemDef, invIndex = null) {
    if (!itemDef) return false;
    try {
      // Get self attribute for scaling potion effects
      const selfAttr = Math.max(0, Number(this.self || 0));
      const selfMultiplier = 1 + (selfAttr / 100); // e.g., self=50 gives 1.5x multiplier
      
      switch (itemDef.type) {
        case 'healing': {
          const baseAmount = itemDef.effect?.heal || 0;
          const scaledAmount = Math.max(1, Math.floor(baseAmount * selfMultiplier));
          const healed = scaledAmount > 0 ? this.heal(scaledAmount) : 0;
          this.lastItemHealAmount = healed;
          break;
        }
        case 'ammo':
        case 'arrow': {
          const arrowsPerUse = Number(itemDef.effect?.arrows ?? 0);
          if (arrowsPerUse > 0) {
            this.addArrows(arrowsPerUse);
          }
          break;
        }
        case 'mana': {
          const baseMana = itemDef.effect?.mana || 0;
          const scaledMana = Math.floor(baseMana * selfMultiplier);
          const gained = this.restoreMana(scaledMana);
          this.lastItemManaAmount = gained;
          break;
        }
        case 'strength': {
          const baseBoost = itemDef.effect?.attackBoost || 0;
          const scaledBoost = baseBoost * selfMultiplier;
          const durationSec = itemDef.effect?.durationSec || 10;
          const now = Date.now();
          this.commandState = this.commandState || {};
          const until = now + durationSec * 1000;
          this.commandState.strengthBuff = {
            attackBoost: scaledBoost,
            until
          };
          break;
        }
        case 'poison': {
          // Create a ground hazard at player's feet
          try {
            const x = this.sprite.x;
            const y = this.sprite.y;
            const baseEffect = itemDef.effect || {};
            // Scale poison DOT by self attribute
            const scaledEffect = {
              ...baseEffect,
              dot: baseEffect.dot ? Math.floor(baseEffect.dot * selfMultiplier) : baseEffect.dot
            };
            if (this.scene && this.scene.lootSpawner && typeof this.scene.lootSpawner.spawnPoisonDot === 'function') {
              this.scene.lootSpawner.spawnPoisonDot(x, y, scaledEffect, this);
            }
          } catch (_) {}
          break;
        }
        default:
          break;
      }

      // Consume loot first, then inventory
      // Step 1: Try to consume from player.loot
      let consumed = false;
      let consumptionType = null;
      
      if (Array.isArray(this.loot)) {
        const lootEntry = this.loot.find(e => e.itemId === itemDef.id);
        if (lootEntry && (lootEntry.qty || 0) > 0) {
          // Consume from loot
          lootEntry.qty = Math.max(0, (lootEntry.qty || 1) - 1);
          if (lootEntry.qty <= 0) {
            const lootIdx = this.loot.indexOf(lootEntry);
            if (lootIdx >= 0) this.loot.splice(lootIdx, 1);
          }
          consumptionType = 'loot';
          incrementConsumptionRecord(this.consumptionTotals, itemDef.id, 'loot');
          consumed = true;
          console.log(`[Player.consumeItem] 🗑️ Consumed LOOT item: ${itemDef.id}, remaining lootQty: ${lootEntry.qty || 0}`);
        }
      }

      // Step 2: If no loot available, consume from inventory
      if (!consumed && Array.isArray(this.inventory)) {
        let idx = invIndex;
        if (idx == null) {
          idx = this.inventory.findIndex(e => e.itemId === itemDef.id);
        }
        if (idx >= 0) {
          const entry = this.inventory[idx];
          ensureInventoryEntryMetadata(entry);
          const currentQty = Math.max(0, toNumber(entry.qty, 0));
          if (currentQty > 0) {
            const newQty = Math.max(0, currentQty - 1);
            
            // Consume from original inventory
            entry.originalQty = Math.max(0, toNumber(entry.originalQty || currentQty, currentQty) - 1);
            entry.consumedOriginal = (entry.consumedOriginal || 0) + 1;
            incrementConsumptionRecord(this.consumptionTotals, entry.itemId, 'original');
            consumptionType = 'original';
            
            entry.qty = newQty;
            entry.quantity = newQty;

            if (newQty <= 0) {
              this.inventory.splice(idx, 1);
            }
            
            consumed = true;
            console.log(`[Player.consumeItem] 🗑️ Consumed ORIGINAL item: ${entry.itemId}, remaining originalQty: ${entry.originalQty}, total consumed: ${entry.consumedOriginal}`);

            // Send real-time consumption event via socket (only for original items, not loot)
            if (this.heroId && this.scene?.gameManager?.currentBattleId) {
              if (!socketService.isSocketConnected()) {
                console.error('[Player.consumeItem] ⚠️ Socket not connected! Potion consumption will not be tracked in real-time.');
                console.error('[Player.consumeItem] Make sure socket is connected on the game page.');
              } else {
                try {
                  socketService.consumePotion({
                    heroId: this.heroId,
                    battleId: this.scene.gameManager.currentBattleId,
                    itemId: entry.itemId,
                    itemType: itemDef.type,
                    quantity: 1,
                    timestamp: Date.now()
                  });
                } catch (err) {
                  console.error('[Player.consumeItem] Failed to send potion consumption via socket:', err);
                }
              }
            }
          }
        }
      }

      if (!consumed) {
        console.warn(`[Player.consumeItem] ⚠️ Could not consume item ${itemDef.id} - not found in loot or inventory`);
        return false;
      }
  const isHealing =
    (itemDef.type && itemDef.type.toLowerCase && itemDef.type.toLowerCase() === 'healing') ||
    (itemDef.effect && typeof itemDef.effect === 'object' && itemDef.effect.heal != null);
  if (this.isAI && isHealing) {
    this.commandState = this.commandState || {};
    this.commandState.nextHealingPotionAt = this.getNow() + 15000;
  }
      if (this.scene && typeof this.scene.refreshSidebarForPlayer === 'function') {
        try { this.scene.refreshSidebarForPlayer(this); } catch (_) {}
      }
      return true;
    } catch (_) {
      return false;
    }
  }

  // Determine approximate combat state
  isInCombatOrThreatened(maxDistanceMul = 1.2) {
    try {
      if (this.isBattling) return true;
      const meleeReach = this.meleeRange || 52;
      const maxDist = meleeReach * (typeof maxDistanceMul === 'number' ? maxDistanceMul : 1.2);
      const players = this.scene?.players || [];
      for (let i = 0; i < players.length; i++) {
        const other = players[i];
        if (!other || other === this || !other.isAlive) continue;
        const d = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, other.sprite.x, other.sprite.y);
        if (d <= maxDist) return true;
      }
    } catch (_) {}
    return false;
  }

  // On-pickup immediate consideration to consume the picked item
  considerUsingPickedItem(itemDef) {
    if (!itemDef || !this.isAlive) return false;
    if (!this.isAI) return false;
    const usageFactor = this.getItemUsageFactor();
    const healthPct = Math.max(0, Math.min(1, this.health / (this.maxHealth || 1)));
    const inCombat = this.isInCombatOrThreatened(1.3);
    const type = (itemDef.type || '').toLowerCase();
    switch (type) {
      case 'healing': {
        const now = this.getNow();
        if (this.commandState?.nextHealingPotionAt && now < this.commandState.nextHealingPotionAt) {
          break;
        }
        const threshold = lerp(0.25, 0.7, usageFactor);
        if (healthPct < threshold) return this.consumeItem(itemDef);
        break;
      }
      case 'strength':
      case 'battle': {
        const now = this.getNow();
        const active = this.commandState?.strengthBuff && now < this.commandState.strengthBuff.until;
        if (!active && inCombat) {
          const useChance = lerp(0.05, 0.75, usageFactor);
          if (Math.random() < useChance) return this.consumeItem(itemDef);
        }
        break;
      }
      case 'ammo':
      case 'arrow': {
        const ammoGain = Number(itemDef.effect?.arrows ?? 0);
        const maxArrows = Math.max(1, this.maxArrows || 0);
        const currentRatio = maxArrows > 0 ? (this.arrowCount || 0) / maxArrows : 0;
        const threshold = 0.4 + (this.strategyItemUse / 100) * 0.4; // 40%..80%
        if (ammoGain > 0 && currentRatio < threshold) {
          return this.consumeItem(itemDef);
        }
        break;
      }
      case 'mana': {
        if ((this.maxMana || 0) > 0) {
          const manaPct = Math.max(0, Math.min(1, (this.mana || 0) / (this.maxMana || 1)));
          const threshold = lerp(0.2, 0.6, usageFactor);
          if (manaPct < threshold) return this.consumeItem(itemDef);
        }
        break;
      }
      default: {
        if (itemDef.effect && typeof itemDef.effect === 'object' && itemDef.effect.rage != null) {
        const now = this.getNow();
          const active = this.commandState?.strengthBuff && now < this.commandState.strengthBuff.until;
          if (!active && inCombat) {
            const useChance = lerp(0.05, 0.75, usageFactor);
            if (Math.random() < useChance) return this.consumeItem(itemDef);
          }
        }
        break;
      }
    }
    return false;
  }

  // Periodic best-choice auto-use (called ~every 10s by scene)
  tryAutoUseBestItem() {
    if (!this.isAlive || !this.isAI) return false;
    const itemsById = this.scene?.itemsById;
    if (!itemsById || typeof itemsById.get !== 'function') return false;

    const now = this.getNow();
    if (now - (this.lastAutoUseAt || 0) < 2000) return false; // simple per-player rate limit

    const inv = Array.isArray(this.inventory) ? this.inventory : [];
    if (!inv.length) return false;

    const healthPct = Math.max(0, Math.min(1, this.health / (this.maxHealth || 1)));
    const inCombat = this.isInCombatOrThreatened(1.3);
    const usageFactor = this.getItemUsageFactor();

    // 1) Healing if needed: choose highest heal amount
    const healThreshold = lerp(0.25, 0.7, usageFactor);
    if (healthPct < healThreshold) {
      // Check for ultimate usage (25% chance when healing check is triggered)
      if (this.canUseUltimate() && Math.random() < 0.25) {
        this.lastAutoUseAt = now;
        if (this.useUltimate()) {
          return true; // Ultimate was used successfully
        }
      }
      let bestIdx = -1; let bestHeal = 0; let bestDef = null;
      inv.forEach((e, idx) => {
        const def = itemsById.get(e.itemId);
        if (def && def.type === 'healing' && (e.qty || 0) > 0) {
          const heal = Math.max(0, def.effect?.heal || 0);
          if (heal > bestHeal) { bestHeal = heal; bestIdx = idx; bestDef = def; }
        }
      });
      if (bestIdx >= 0 && bestDef) {
        const nowMs = this.getNow();
        if (this.commandState?.nextHealingPotionAt && nowMs < this.commandState.nextHealingPotionAt) {
          // skip due to cooldown
        } else {
          this.lastAutoUseAt = now;
          return this.consumeItem(bestDef, bestIdx);
        }
      }
    }

    // 2) Strength in combat to gain advantage if no active buff
    if (inCombat) {
      const hasBuff = !!(this.commandState?.strengthBuff && now < this.commandState.strengthBuff.until);
      if (!hasBuff) {
        let bestIdx = -1; let bestBoost = 0; let bestDef = null;
        inv.forEach((e, idx) => {
          const def = itemsById.get(e.itemId);
          if (def && (def.type === 'strength' || def.type === 'battle' || (def.effect && def.effect.rage != null)) && (e.qty || 0) > 0) {
            const boost = Math.max(0, def.effect?.attackBoost || 0);
            if (boost > bestBoost) { bestBoost = boost; bestIdx = idx; bestDef = def; }
          }
        });
        if (bestIdx >= 0 && bestDef) {
          const chance = lerp(0.05, 0.75, usageFactor);
          if (Math.random() < chance) {
            this.lastAutoUseAt = now;
            return this.consumeItem(bestDef, bestIdx);
          }
        }
      }
    }

    // 3) Mana if caster and low mana
    if ((this.maxMana || 0) > 0) {
      const manaPct = Math.max(0, Math.min(1, (this.mana || 0) / (this.maxMana || 1)));
      const manaThreshold = lerp(0.2, 0.6, usageFactor);
      if (manaPct < manaThreshold) {
        let bestIdx = -1; let bestMana = 0; let bestDef = null;
        inv.forEach((e, idx) => {
          const def = itemsById.get(e.itemId);
          if (def && def.type === 'mana' && (e.qty || 0) > 0) {
            const mana = Math.max(0, def.effect?.mana || 0);
            if (mana > bestMana) { bestMana = mana; bestIdx = idx; bestDef = def; }
          }
        });
        if (bestIdx >= 0 && bestDef) {
          this.lastAutoUseAt = now;
          return this.consumeItem(bestDef, bestIdx);
        }
      }
    }
    return false;
  }

  updateVisualComponents(alivePlayers) {
      // Update taunt timer and position
      if (this.tauntTimer > 0) {
        this.tauntTimer -= 16.67; // Approximate frame time
        if (this.tauntTimer <= 0) {
          this.hideTaunt();
        } else {
          this.updateTauntPosition();
        }
      }

      // Update text and health bar positions
      const s = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;
      this.nameText.x = this.sprite.x;
      this.nameText.y = this.sprite.y - 43 * s;
      this.healthBarBg.x = this.sprite.x;
      this.healthBarBg.y = this.sprite.y - 33 * s;
      this.healthBar.x = this.sprite.x;
      this.healthBar.y = this.sprite.y - 33 * s;
      
      // Update ultimate icon position (to the left of health bar)
      if (this.ultimateIcon) {
        const healthBarWidth = 39 * s;
        const iconX = this.sprite.x - healthBarWidth / 2 - 1 * s; // 8px to the left of health bar
        const iconY = this.sprite.y - 30 * s; // Same Y as health bar
        this.ultimateIcon.x = iconX;
        this.ultimateIcon.y = iconY;
      }
      
      // Update ultimate glow position if active
      if (this.ultimateGlow && this.ultimateGlowUpdate) {
        this.ultimateGlow.x = this.sprite.x;
        this.ultimateGlow.y = this.sprite.y;
      }
      
      // Update area indicator position if active (for area burst)
      if (this.ultimateAreaIndicator && this.sprite) {
        this.ultimateAreaIndicator.x = this.sprite.x;
        this.ultimateAreaIndicator.y = this.sprite.y;
      }

      // Hide player names if more than 200 players are alive
      this.nameText.setVisible(alivePlayers <= 200);

      // Update shield position and visibility for top holders
      // Shields always show for top holders, unlike names which hide with many players
      const shieldData = getShieldForPlayer(this, this.scene.topHolders || []);
      if (shieldData && this.isAlive) {
        this.shieldSprite.setTexture(shieldData.id);
        this.shieldSprite.x = this.sprite.x;
        this.shieldSprite.y = this.sprite.y - 50 * s;
        this.shieldSprite.setVisible(true); // Always visible for top holders
      } else {
        this.shieldSprite.setVisible(false);
      }
  }

  updateHealthBar() {
    this.healthBar.clear();
    this.healthBarBg.clear();
    const scale = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;
    const width = 39 * scale;
    const height = 6 * scale;
    const x = -width / 2;
    const y = 0;
    // Background
    this.healthBarBg.fillStyle(0x000000, 0.5);
    this.healthBarBg.fillRect(x, y, width, height);
    // Health fill
    const healthPercent = this.health / this.maxHealth;
    let color;
    if (healthPercent < 0.33) {
      color = 0xff3333; // Red
    } else if (healthPercent < 0.66) {
      color = 0xffff33; // Yellow
    } else {
      color = 0x33ff33; // Green
    }
    this.healthBar.fillStyle(color, 1);
    this.healthBar.fillRect(x, y, width * healthPercent, height);
    
    // Update ultimate icon visibility and text
    if (this.ultimateIcon) {
      const iconMap = { damage_burst: '⚔️', area_burst: '💥' };
      const iconText = iconMap[this.ultimateType] || '⭐';
      this.ultimateIcon.setText(iconText);
      
      // Show icon only when ultimate is ready (charge >= 100) and player is alive
      this.ultimateIcon.setVisible(this.ultimateCharge >= 100 && this.isAlive);
    }
  }

  // Team system methods
  setTeam(teamId, teamColor) {
    this.team = teamId;
    this.teamColor = teamColor;
    this.updateTeamVisuals();
  }

  updateTeamVisuals() {
    if (this.team && this.teamColor && this.nameText) {
      // Update name text color to team color
      this.nameText.setColor(this.teamColor);
    } else if (this.nameText) {
      // Reset to default white color
      this.nameText.setColor('#ffffff');
    }
  }

  isTeammate(otherPlayer) {
    return this.team && otherPlayer.team && this.team === otherPlayer.team;
  }

  canAttack(otherPlayer) {
    // Can't attack teammates (unless in free-for-all mode)
    if (this.isTeammate(otherPlayer)) {
      return false;
    }
    return true;
  }

  /**
   * Show a chat bubble taunt above the player
   * @param {string} tauntText - Text to display in bubble
   * @param {number} duration - How long to show the bubble (ms)
   * @param {Function} onComplete - Callback when bubble is hidden
   */
  showTaunt(tauntText, duration = 1500, onComplete = null) {
    // Hide existing taunt if present
    this.hideTaunt();

    if (!tauntText || !this.sprite) return;

    const objectScale = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;

    // Create text first to measure size
    this.tauntText = this.scene.add.text(0, 0, tauntText, {
      fontFamily: 'Comic Sans MS, Comic Sans, cursive',
      fontSize: '36px',
      color: '#000000',
      align: 'center',
      wordWrap: { width: 350 }
    }).setOrigin(0.5).setDepth(14);

    // Calculate bubble size based on text bounds
    const textBounds = this.tauntText.getBounds();
    const padding = 15;
    const bubbleWidth = textBounds.width + padding * 2;
    const bubbleHeight = textBounds.height + padding * 2;

    // Create bubble background
    this.tauntBubble = this.scene.add.graphics();
    
    // Draw white rounded rectangle with black border
    this.tauntBubble.fillStyle(0xFFFFFF, 1.0);
    this.tauntBubble.lineStyle(2, 0x000000, 1.0);
    this.tauntBubble.fillRoundedRect(
      -bubbleWidth / 2,
      -bubbleHeight / 2,
      bubbleWidth,
      bubbleHeight,
      10
    );
    this.tauntBubble.strokeRoundedRect(
      -bubbleWidth / 2,
      -bubbleHeight / 2,
      bubbleWidth,
      bubbleHeight,
      10
    );

    // Position bubble just above nameText (nameText is at sprite.y - 43 * objectScale)
    // Position the bubble so it sits right above the name
    const bubbleY = this.sprite.y - 43 * objectScale - bubbleHeight / 2 - 5;
    const bubbleX = this.sprite.x;
    
    this.tauntBubble.setPosition(bubbleX, bubbleY);
    this.tauntText.setPosition(bubbleX, bubbleY);
    
    // Set depth based on Y position for natural stacking (higher Y = closer to camera = on top)
    // Base depth is 1000, add Y position to ensure proper ordering
    // Bubbles lower on screen (higher Y) will render on top of bubbles higher on screen (lower Y)
    const bubbleDepth = 1000 + this.sprite.y;
    const textDepth = bubbleDepth + 1; // Text always on top of its bubble
    this.tauntBubble.setDepth(bubbleDepth);
    this.tauntText.setDepth(textDepth);

    // Ensure UI camera ignores bubble elements
    if (this.scene.uiCamera && this.scene.uiCamera.ignore) {
      this.scene.uiCamera.ignore(this.tauntBubble);
      this.scene.uiCamera.ignore(this.tauntText);
    }

    // Set timer and callback
    this.tauntTimer = duration;
    this.tauntOnComplete = onComplete;
  }

  /**
   * Hide the chat bubble taunt
   */
  hideTaunt() {
    if (this.tauntBubble) {
      this.tauntBubble.destroy();
      this.tauntBubble = null;
    }
    if (this.tauntText) {
      this.tauntText.destroy();
      this.tauntText = null;
    }
    
    // Call completion callback
    if (this.tauntOnComplete) {
      this.tauntOnComplete();
      this.tauntOnComplete = null;
    }
    
    this.tauntTimer = 0;
  }

  /**
   * Update taunt bubble position to follow player
   */
  updateTauntPosition() {
    if (!this.tauntBubble || !this.tauntText || !this.sprite) return;

    const objectScale = (typeof this.scene.objectScale === 'number') ? this.scene.objectScale : 1.0;
    
    // Get bubble height from text bounds
    const textBounds = this.tauntText.getBounds();
    const padding = 15;
    const bubbleHeight = textBounds.height + padding * 2;
    
    // Position just above nameText (nameText is at sprite.y - 43 * objectScale)
    const bubbleY = this.sprite.y - 43 * objectScale - bubbleHeight / 2 - 5;
    const bubbleX = this.sprite.x;

    this.tauntBubble.setPosition(bubbleX, bubbleY);
    this.tauntText.setPosition(bubbleX, bubbleY);
    
    // Update depth based on Y position for natural stacking
    const bubbleDepth = 1000 + this.sprite.y;
    const textDepth = bubbleDepth + 1;
    this.tauntBubble.setDepth(bubbleDepth);
    this.tauntText.setDepth(textDepth);
  }

  // Effective stat getter methods (base + modifiers)
  getEffectiveStrength() { return this.strength + this.statModifiers.strength; }
  getEffectiveEndurance() { return this.endurance + this.statModifiers.endurance; }
  getEffectiveCoordination() { return this.coordination + this.statModifiers.coordination; }
  getEffectiveQuickness() { return this.quickness + this.statModifiers.quickness; }
  getEffectiveFocus() { return this.focus + this.statModifiers.focus; }
  getEffectiveSelf() { return this.self + this.statModifiers.self; }

  // Stat modifier management methods
  applyStatModifier(modifierName, value, durationMs = null) {
    if (this.statModifiers.hasOwnProperty(modifierName)) {
      this.statModifiers[modifierName] += value;
      
      if (durationMs) {
        setTimeout(() => {
          this.removeStatModifier(modifierName, value);
        }, durationMs);
      }
    }
  }

  removeStatModifier(modifierName, value) {
    if (this.statModifiers.hasOwnProperty(modifierName)) {
      this.statModifiers[modifierName] -= value;
    }
  }

  clearStatModifiers() {
    Object.keys(this.statModifiers).forEach(key => {
      this.statModifiers[key] = 0;
    });
  }

  // Calculate damage with weapon and stats
  calculateMeleeDamage() {
    // Get weapon damage range or use default
    let baseDamage = 5;
    let maxDamage = 10;
    
    if (this.equippedWeapon && this.equippedWeapon.damage) {
      baseDamage = this.equippedWeapon.damage.min;
      maxDamage = this.equippedWeapon.damage.max;
    }
    
    const weaponDamage = baseDamage + Math.random() * (maxDamage - baseDamage);
    
    // Strength multiplier: +0.5% per point above 10
    const strengthMultiplier = 1 + (this.getEffectiveStrength() - 10) * 0.005;
    
    // Apply stat modifiers
    const flatBonus = this.statModifiers.damage;
    
    let total = (weaponDamage * strengthMultiplier) + flatBonus;
    // Apply temporary strength potion buff if active
    try {
      const now = Date.now();
      const buff = this.commandState && this.commandState.strengthBuff;
      if (buff && now < (buff.until || 0)) {
        const mul = 1 + Math.max(0, buff.attackBoost || 0);
        total *= mul;
      }
    } catch (_) {}

    // Apply charging bonus: 1.5x if charging, 2x if charging + rage
    if (this.isCharging) {
      try {
        const now = Date.now();
        const rageUntil = this.commandState?.rageUntil || 0;
        const hasRage = now < rageUntil;
        if (hasRage) {
          total *= 2.0; // Double damage if charging + rage
        } else {
          total *= 1.5; // 1.5x damage if just charging
        }
      } catch (_) {}
    }

    const aggressionFactor = typeof this.strategyAggression === 'number' ? this.strategyAggression : 50;
    const quickness = this.getEffectiveQuickness ? this.getEffectiveQuickness() : (this.quickness || 10);
    let critChance = 0.05;
    critChance += Math.max(0, aggressionFactor - 50) * 0.001;
    critChance += Math.max(0, quickness - 10) * 0.0005;
    critChance = Math.min(0.35, Math.max(0, critChance));
    const isCritical = Math.random() < critChance;
    if (isCritical) {
      total *= 1.5;
    }
    this.lastAttackWasCritical = isCritical;
    return total;
  }

  // Calculate damage reduction from coordination and armor
  calculateDamageReduction(incomingDamage) {
    // Coordination reduces damage: 0.5% per point above 10
    const coordReduction = (this.getEffectiveCoordination() - 10) * 0.005;
    
    // Armor defense bonus
    let armorDefense = 0;
    if (this.equippedArmor.head) armorDefense += this.equippedArmor.head.defense || 0;
    if (this.equippedArmor.chest) armorDefense += this.equippedArmor.chest.defense || 0;
    if (this.equippedArmor.legs) armorDefense += this.equippedArmor.legs.defense || 0;
    
    const defenseBonus = this.statModifiers.defense + armorDefense;
    const totalReduction = Math.min(0.75, coordReduction + defenseBonus); // Cap at 75%
    let reduced = incomingDamage * (1 - totalReduction);
    const fatigueMultiplier = this.getFatigueDamageMultiplier();
    if (fatigueMultiplier > 1) {
      reduced *= fatigueMultiplier;
    }
    return reduced;
  }

  showDamageNumber(amount, { critical = false, heal = false } = {}) {
    if (!this.scene || !this.sprite || !Number.isFinite(amount) || amount <= 0) return;

    const os = (typeof this.scene.objectScale === 'number' && this.scene.objectScale > 0) ? this.scene.objectScale : 1;
    const textValue = Math.max(1, Math.round(amount));
    const offsetX = -6 + Phaser.Math.FloatBetween(-6, 6) * os;
    const offsetY = -45 * os + (Math.random() * -10);
    const baseFontSize = critical || heal ? 26 : 20;
    const fontSize = Math.max(12, baseFontSize * Math.min(os * 0.9, 1.4));
    const color = heal ? '#6be477' : (critical ? '#ff4d4d' : '#ffca28');
    const strokeColor = heal ? '#1f6b2c' : (critical ? '#8b0000' : '#7f3600');
    const prefix = heal ? '+' : '-';

    const damageText = this.scene.add.text(
      this.sprite.x + offsetX,
      this.sprite.y + offsetY,
      `${prefix}${textValue}`,
      {
        fontFamily: 'Arial Black, Arial, sans-serif',
        fontSize: `${fontSize}px`,
        color,
        stroke: strokeColor,
        strokeThickness: 4,
        align: 'center'
      }
    ).setDepth(25);

    const floatDistance = 40 * os;
    const floatDuration = heal ? 1300 : (critical ? 1600 : 1400);
    const fadeDelay = floatDuration * 0.55;
    const fadeDuration = floatDuration - fadeDelay;

    this.scene.tweens.add({
      targets: damageText,
      y: damageText.y - floatDistance,
      ease: 'Sine.easeOut',
      duration: floatDuration
    });

    this.scene.time.delayedCall(fadeDelay, () => {
      this.scene.tweens.add({
        targets: damageText,
        alpha: 0,
        ease: 'Sine.easeIn',
        duration: fadeDuration,
        onComplete: () => {
          try { damageText.destroy(); } catch (_) {}
        }
      });
    });
  }

  buildInventorySyncState(containerCapacity = DEFAULT_CONTAINER_CAPACITY) {
    const inventoryEntries = Array.isArray(this.inventory) ? this.inventory : [];
    if (!inventoryEntries.length) {
      return {
        slots: [],
        consumption: this.getConsumptionSummary(),
      };
    }

    const slots = inventoryEntries
      .filter(Boolean)
      .map((entry) => {
        ensureInventoryEntryMetadata(entry);
        return {
          id: entry.id ?? entry.inventoryId ?? null,
          itemId: entry.itemId || entry.themeItemId || entry.id || null,
          itemType: entry.itemType ?? entry.item_type ?? null,
          quantity: toNumber(entry.qty, 0),
          originalQty: toNumber(entry.originalQty, toNumber(entry.qty, 0)),
          lootQty: toNumber(entry.lootQty, 0),
          consumedOriginal: toNumber(entry.consumedOriginal, 0),
          consumedLoot: toNumber(entry.consumedLoot, 0),
          themeId: entry.themeId ?? entry.theme_id ?? null,
        };
      })
      .filter((slot) => slot.itemId && slot.quantity > 0);

    return {
      slots,
      consumption: this.getConsumptionSummary(),
    };
  }
}
